import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { talents, stages, jobApplications, libraries, users, talentMedia, jobs } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import useFormPersistence from '../../hooks/useFormPersistence';
import { getTalentTypeOptions } from '../../utils/talentTypeUtils';
import { getCVTypeOptions, getCVTypePlaceholders } from '../../utils/cvTypeUtils';
import PublicProfileSection from '../Talent/PublicProfileSection';
import { calculateIsActive } from '../../utils/talentStatusUtils';
import { DEPARTURE_DATE_TYPES } from '../../utils/dateTypeUtils';
import {
  getProfessionName,
  getDepartmentName,
  getDepartmentCode,
  getPositionName,
  getSimilarTermName
} from '../../utils/professionUtils';
import { TextField, DatePicker, Checkbox, Autocomplete } from '@anyemp/ui-kit';
import ContactForm from './ContactForm';
import FileUpload from './FileUpload';
import ImportantDatesManager from './ImportantDatesManager';
import AISettingsManager from './AISettingsManager';
import FormSection from './FormSection';
import { FormSidebar, MobileFormNav } from './FormSidebar';
import ConfirmDialog from './ConfirmDialog';
import {
  ArrowLeft,
  Save,
  User,
  Phone,
  Briefcase,
  Plus,
  AlertCircle,
  CheckCircle,
  X,
  FileText,
  Settings,
  Globe,
  Wrench,
  ClipboardList,
  Languages,
  Image,
  Calendar,
  Bot,
  DollarSign,
  Info
} from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import ResumeParserDrawer from './ResumeParserDrawer';
import ParsedDataPanel from './ParsedDataPanel';
import ResumeConflictDialog from './ResumeConflictDialog';
import FormActions from './FormActions';
import { mapResumeDataToTalentForm, normalizeContactValue } from '../../utils/resumeDataMapper';
import { normalizeResponse } from '../../utils/normalizeResponse';

// Section configuration for sidebar navigation
const FORM_SECTIONS = [
  { id: 'basic-info',      label: 'Basic Information',     icon: User,         required: true },
  { id: 'contacts',        label: 'Contacts',              icon: Phone,        required: true },
  { id: 'settings',        label: 'Additional Settings',   icon: Settings,     required: false },
  { id: 'public-settings', label: 'Public Settings',       icon: Globe,        required: 'conditional' }, // Required only if public_visibility is true
  { id: 'professions',     label: 'Professions',           icon: Briefcase,    required: true },
  { id: 'tools',            label: 'Tools & Technologies',  icon: Wrench,         required: false },
  { id: 'responsibilities', label: 'Responsibilities',       icon: ClipboardList,  required: false },
  { id: 'cvs',             label: 'CVs',                   icon: FileText,     required: false },
  { id: 'languages',       label: 'Languages',             icon: Languages,    required: true },
  { id: 'media',           label: 'Media Files',           icon: Image,        required: false },
  { id: 'dates',           label: 'Important Dates',       icon: Calendar,     required: false },
  { id: 'ai-settings',     label: 'AI Settings',           icon: Bot,          required: false },
  { id: 'salary',           label: 'Salary',                icon: DollarSign,   required: false },
];

// Hook for tracking active section via Intersection Observer
const useActiveSectionObserver = (sectionIds) => {
  const [activeSection, setActiveSection] = useState(sectionIds[0]);
  const isManualScrolling = useRef(false);

  const setActiveSectionManually = useCallback((sectionId) => {
    setActiveSection(sectionId);
    isManualScrolling.current = true;
    // Allow observer to take over after scroll animation completes
    setTimeout(() => {
      isManualScrolling.current = false;
    }, 1000);
  }, []);

  useEffect(() => {
    const updateActiveSection = () => {
      if (isManualScrolling.current) return;

      // Find the section that is closest to the top of the viewport
      let closestSection = null;
      let closestDistance = Infinity;

      sectionIds.forEach(id => {
        const el = document.getElementById(id);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        // Section is visible if it's within viewport
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          // Distance from top of viewport (prefer sections near top)
          const distance = Math.abs(rect.top);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = id;
          }
        }
      });

      if (closestSection) {
        setActiveSection(closestSection);
      }
    };

    // Update on scroll
    const handleScroll = () => {
      requestAnimationFrame(updateActiveSection);
    };

    // Initial update
    updateActiveSection();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sectionIds.join(',')]);

  return [activeSection, setActiveSectionManually];
};

const TalentForm = ({
  initialData = {},
  onSubmit,
  onCancel,
  onSuccess,
  title = "Create Talent",
  submitText = "Create",
  showHeader = true,
  jobApplicationInfo = null,
  isModal = false,
  talentId = null,
  isMyProfile = false
}) => {
  const queryClient = useQueryClient();
  const { user, hasPermission } = useAuth();

  // Получаем talentId из параметра или из initialData
  const currentTalentId = talentId || initialData.id;

  // Состояние формы (исключаем publicSettings из initialData)
  const { publicSettings: _, ...initialDataWithoutPublicSettings } = initialData;
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    country_id: '',
    city_id: '',
    talent_type: 'candidate',
    current_status_id: '',
    current_stage_id: '',
    public_visibility: true,
    is_active: true, // Добавляем поле для статуса активности
    auth_user_id: '', // Добавляем поле для выбора пользователя
    manager_id: !currentTalentId && user?.id ? user.id : (initialDataWithoutPublicSettings.manager_id || ''), // Auto-populate with current user when creating
    talent_id: '', // Добавляем поле для talent_id
    birth_year: '',
    job_post_id: '', // Привязка shadow-JobApplication к job-post (только при создании)
    ...initialDataWithoutPublicSettings
  });

  // Логирование для отладки (перемещено после объявления всех переменных)

  // Синхронизация формы с initialData при изменении
  useEffect(() => {
    if (initialData && initialData.id) {
      setFormData(prev => {
        // Проверяем, действительно ли данные изменились
        const hasChanges = Object.keys(initialData).some(key => 
          prev[key] !== initialData[key]
        );
        
        if (hasChanges) {
          const newData = {
            ...prev,
            ...initialData
          };
          return newData;
        }
        
        return prev;
      });
    }
  }, [initialData?.id, initialData?.current_status_id, initialData?.current_stage_id]); // Зависим только от ключевых полей

  // Синхронизация publicSettings с initialData
  useEffect(() => {
    if (initialData && initialData.publicSettings) {
      setPublicSettings(initialData.publicSettings);
    }
  }, [initialData?.publicSettings?.id]); // Обновляем при изменении publicSettings

  // Синхронизация importantDates с initialData
  useEffect(() => {
    if (initialData && initialData.importantDates) {
      setImportantDates(initialData.importantDates);
    }
  }, [initialData?.importantDates]); // Обновляем при изменении importantDates

  // Синхронизация aiSettings с initialData
  useEffect(() => {
    if (initialData && initialData.aiSettings) {
      setAiSettings(initialData.aiSettings);
    }
  }, [initialData?.aiSettings]); // Обновляем при изменении aiSettings

  // Синхронизация salary с initialData
  useEffect(() => {
    if (initialData && initialData.salary) {
      setSalary(initialData.salary);
    }
  }, [initialData?.salary?.id]); // Обновляем при изменении salary

  // Auto-populate manager with current user when creating new talent
  useEffect(() => {
    if (!currentTalentId && user?.id && !formData.manager_id) {
      setFormData(prev => ({
        ...prev,
        manager_id: user.id
      }));
    }
  }, [currentTalentId, user?.id]);

  // Состояние для связанных сущностей
  const [professions, setProfessions] = useState(() => {
    // Нормализуем названия по агрегированным данным — дальше по форме (превью talent_id,
    // отображение, payload на сохранение) везде используются уже свежие значения
    const initial = (initialData.professions || []).map(p => ({
      ...p,
      profession_name: getProfessionName(p),
      department_name: getDepartmentName(p),
      department_code: getDepartmentCode(p),
      position_name: getPositionName(p),
      similar_term_name: getSimilarTermName(p)
    }));
    // Always ensure at least one profession entry
    if (initial.length === 0) {
      return [{
        profession_id: null,
        profession_name: '',
        department_id: null,
        department_name: '',
        position_id: null,
        position_name: '',
        similar_term_id: null,
        similar_term_name: '',
        is_similar: false,
        is_primary: true,
        public_visible: true
      }];
    }
    // Ensure first profession is primary if none is marked
    if (!initial.some(p => p.is_primary)) {
      return initial.map((p, i) => (i === 0 ? { ...p, is_primary: true } : p));
    }
    return initial;
  });
  const [selectedTools, setSelectedTools] = useState(
    [...new Set(initialData.tools?.map(tool => tool.tool_id) || [])]
  ); // Массив ID выбранных инструментов (поддерживаем оба формата)
  const [selectedResponsibilities, setSelectedResponsibilities] = useState(
    initialData.responsibilities?.map(r => r.responsibility_id) || []
  );
  const [cv, setCV] = useState(initialData.cv || []);
  const [contacts, setContacts] = useState(initialData.contacts || []);
  const [media, setMedia] = useState(initialData.media || []);
  const [importantDates, setImportantDates] = useState(initialData.importantDates || []);
  const [aiSettings, setAiSettings] = useState(initialData.aiSettings || []);
  const [languages, setLanguages] = useState(initialData.languages || []);
  const [publicSettings, setPublicSettings] = useState(initialData.publicSettings || {
    seo_title: '',
    seo_description: '',
    slug: '',
    rate_id: null,
    short_name: '',
    is_pinned: false,
    slogan: ''
  });
  const [salary, setSalary] = useState(initialData.salary || {
    expectations_amount: null,
    expectations_currency_id: null,
    proposed_salary_amount: null,
    proposed_salary_currency_id: null,
    shift_id: null,
    rate_id: null
  });
  
  // Состояние для countries и cities
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [showAllCities, setShowAllCities] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);

  // Состояние для индустрий и суб-индустрий
  const [industriesOptions, setIndustriesOptions] = useState([]);
  const [subIndustriesOptions, setSubIndustriesOptions] = useState([]);
  const [allSubIndustriesOptions, setAllSubIndustriesOptions] = useState([]); // Все суб-индустрии для фильтрации

  // Состояние UI
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [resumeFillInfo, setResumeFillInfo] = useState(null);
  const [isResumeParserOpen, setIsResumeParserOpen] = useState(false);

  // Состояние для AI Parsed Data Panel
  const [aiRawData, setAiRawData] = useState(null);
  const [unmappedData, setUnmappedData] = useState(null);
  const [isParsedDataPanelOpen, setIsParsedDataPanelOpen] = useState(false);

  // Состояние для Conflict Resolution Dialog
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [pendingMappedData, setPendingMappedData] = useState(null);

  // Состояние для Cancel Confirmation Dialog
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [dataRestoredFromStorage, setDataRestoredFromStorage] = useState(false);

  // Определяем, это редактирование или создание нового таланта
  const isEditing = Boolean(currentTalentId);

  // Form persistence - autosave only when creating (not editing)
  const STORAGE_KEY = 'talentFormDraft';
  const { saveToStorage, loadFromStorage, clearStorage } =
    useFormPersistence(STORAGE_KEY, !isEditing);

  // Use ref instead of state - doesn't need to trigger re-renders
  const isFormInitialized = useRef(false);
  const statusAutoSelectedRef = useRef(false);
  const stageAutoSelectedRef = useRef(false);
  const errorAlertRef = useRef(null);

  // Sidebar navigation: visible sections list
  const visibleSectionIds = useMemo(() => {
    const tt = formData.talent_type;

    const ids = ['basic-info', 'contacts'];
    if (!isMyProfile) ids.push('settings');
    if (!isMyProfile && formData.public_visibility) ids.push('public-settings');
    ids.push('professions', 'tools', 'responsibilities', 'cvs', 'languages');
    if (isMyProfile || (tt && hasPermission('talent-service', `${tt}_media`, 'read'))) {
      ids.push('media');
    }
    if (!isMyProfile) ids.push('dates', 'ai-settings');
    if (!isMyProfile && tt && hasPermission('talent-service', `${tt}_salary`, 'read')) {
      ids.push('salary');
    }
    return ids;
  }, [isMyProfile, formData.public_visibility, formData.talent_type, hasPermission]);

  // Sidebar navigation: active section tracking
  const [activeSection, setActiveSection] = useActiveSectionObserver(visibleSectionIds);

  // Sidebar navigation: completion status
  const completionStatus = useMemo(() => {
    // Basic Info: First Name, Last Name, Status are required
    // Manager is also required if not MyProfile
    const basicInfoComplete = !!(
      formData.first_name &&
      formData.first_name.trim() &&
      formData.last_name &&
      formData.last_name.trim() &&
      formData.current_status_id &&
      (isMyProfile || formData.manager_id)
    );

    // Contacts: At least one valid contact AND all contacts must be fully filled (no empty contacts allowed)
    const filledContacts = contacts.filter(contact => {
      const toolIdStr = contact.tool_id ? contact.tool_id.toString().trim() : '';
      const valueStr = contact.value ? contact.value.toString().trim() : '';
      return toolIdStr !== '' || valueStr !== '';
    });

    const allFilledContactsValid = filledContacts.every(contact => {
      const toolIdStr = contact.tool_id ? contact.tool_id.toString().trim() : '';
      const valueStr = contact.value ? contact.value.toString().trim() : '';
      const hasToolId = toolIdStr !== '';
      const hasValue = valueStr !== '';
      // Both must be filled
      return hasToolId && hasValue;
    });

    const hasAtLeastOneValidContact = filledContacts.length > 0 && allFilledContactsValid;
    const contactsComplete = hasAtLeastOneValidContact && filledContacts.length === contacts.length;

    // Settings: No required fields (only checkboxes), always complete
    const settingsComplete = true;

    // Public Settings: Only required if public_visibility is true
    // If public_visibility is false, return null (no indicator)
    const publicSettingsComplete = !formData.public_visibility
      ? null
      : !!(publicSettings.slug && publicSettings.slug.trim());

    // Professions: At least one profession AND all added professions must be valid
    const professionsComplete = professions.length > 0 &&
      professions.some(prof => prof.department_id && prof.profession_id) &&
      professions.every(prof => {
        // Both department and profession must be filled
        return prof.department_id && prof.profession_id;
      });

    // Tools: At least one tool selected (optional, no required check)
    const toolsComplete = selectedTools.length > 0;

    // CVs: At least one CV AND all added CVs must be valid
    const cvsComplete = cv.length > 0 &&
      cv.some(cvItem => cvItem.company_name && cvItem.company_name.trim() && cvItem.cv_type && cvItem.cv_type.trim()) &&
      cv.every(cvItem => {
        // Company name and cv_type are required
        return cvItem.company_name && cvItem.company_name.trim() && cvItem.cv_type && cvItem.cv_type.trim();
      });

    // Languages: At least one language AND all added languages must be valid
    const languagesComplete = languages.length > 0 &&
      languages.some(lang => lang.language_id && lang.level_id) &&
      languages.every(lang => {
        // Both language and level must be selected
        return lang.language_id && lang.level_id;
      });

    // Media: Optional section
    const mediaComplete = media.length > 0;

    // Important Dates: Optional section
    const datesComplete = importantDates.length > 0;

    // AI Settings: Optional section
    const aiSettingsComplete = aiSettings.length > 0;

    // Salary: Optional section - complete if any salary field is filled
    const salaryComplete = !!(
      salary.expectations_amount ||
      salary.proposed_salary_amount ||
      salary.shift_id ||
      salary.rate_id
    );

    return {
      'basic-info':      basicInfoComplete,
      'contacts':        contactsComplete,
      'settings':        settingsComplete,
      'public-settings': publicSettingsComplete,
      'professions':     professionsComplete,
      'tools':           toolsComplete,
      'cvs':             cvsComplete,
      'languages':       languagesComplete,
      'media':           mediaComplete,
      'dates':           datesComplete,
      'ai-settings':     aiSettingsComplete,
      'salary':          salaryComplete,
    };
  }, [
    formData.first_name,
    formData.last_name,
    formData.current_status_id,
    formData.manager_id,
    formData.public_visibility,
    isMyProfile,
    contacts,
    publicSettings.slug,
    professions,
    selectedTools,
    cv,
    languages,
    media,
    importantDates,
    aiSettings,
    salary
  ]);

  // Sidebar navigation: scroll to section
  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Temporarily set active section immediately for responsive UI
      setActiveSection(sectionId);
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const scrollToFirstError = useCallback((errors) => {
    const errorKeys = Object.keys(errors);
    const getSectionForKey = (key) => {
      if (['first_name', 'last_name', 'current_status_id', 'current_stage_id', 'manager_id', 'talent_id'].includes(key)) return 'basic-info';
      if (key.startsWith('cv_')) return 'cvs';
      if (key.startsWith('contact_')) return 'contacts';
      if (key.startsWith('profession_') || key === 'professions_primary') return 'professions';
      if (key.startsWith('language_')) return 'languages';
      return null;
    };
    for (const section of FORM_SECTIONS) {
      if (errorKeys.some(key => getSectionForKey(key) === section.id)) {
        scrollToSection(section.id);
        return;
      }
    }
  }, [scrollToSection]);

  // Загружаем медиа-файлы при изменении talentId (не нужно для my-profile — данные уже в initialData)
  const { data: mediaData, isLoading: mediaLoading } = useQuery({
    queryKey: ['talentMedia', currentTalentId],
    queryFn: () => talentMedia.getTalentMedia(currentTalentId),
    enabled: !!currentTalentId && !isMyProfile,
    staleTime: 0,
  });

  // Обновляем состояние медиа-файлов при загрузке данных (только при редактировании существующего таланта)
  useEffect(() => {
    // Обновляем медиа только если это редактирование существующего таланта
    // При создании нового таланта (currentTalentId === null) не перезаписываем состояние
    if (currentTalentId && mediaData?.success && Array.isArray(mediaData.data)) {
      setMedia(mediaData.data);
    }
  }, [mediaData, currentTalentId]);

  // Валидация
  const [validationErrors, setValidationErrors] = useState({});
  
  // Список статусов
  const [statusesList, setStatusesList] = useState([]);

  // Мемоизированные опции статусов
  const statusOptions = useMemo(() => {
    return statusesList.map((status) => ({
      value: status.id.toString(),
      label: status.name
    }));
  }, [statusesList]);

  // Список стадий — отдельная ось от статуса ("где талант" против "что с ним
  // на этой стадии"). Грузится один раз; у каждой стадии есть talent_type, и
  // он задаёт тип таланта, поэтому в выборе — только стадии выбранного типа
  // (плюс текущая стадия таланта, если она исторически другого типа).
  const [stagesList, setStagesList] = useState([]);

  const stageOptions = useMemo(() => {
    return [...stagesList]
      .filter((stage) => (
        !stage.talent_type ||
        stage.talent_type === formData.talent_type ||
        stage.id === Number(formData.current_stage_id)
      ))
      .sort((a, b) => (a.stage_order || 0) - (b.stage_order || 0))
      .map((stage) => ({
        value: stage.id.toString(),
        label: stage.name
      }));
  }, [stagesList, formData.talent_type, formData.current_stage_id]);

  // Списки для выбора
  const [toolsOptions, setToolsOptions] = useState([]); // Все инструменты (без параметров)
  const [responsibilitiesOptions, setResponsibilitiesOptions] = useState([]); // Все обязанности
  const [contactToolsOptions, setContactToolsOptions] = useState([]); // Инструменты для контактов (с параметрами)
  const [allProfessionsOptions, setAllProfessionsOptions] = useState([]); // Все профессии для фильтрации
  const [allSimilarTermsOptions, setAllSimilarTermsOptions] = useState([]); // Similar термы для профессий
  const [departmentsOptions, setDepartmentsOptions] = useState([]);
  const [positionsOptions, setPositionsOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [languagesOptions, setLanguagesOptions] = useState([]);
  const [languageLevelsOptions, setLanguageLevelsOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [rateOptions, setRateOptions] = useState([]);
  const [jobPostsList, setJobPostsList] = useState([]);

  const jobPostOptions = useMemo(() => {
    return jobPostsList.map((p) => ({ value: String(p.id), label: p.name || `Job Post #${p.id}` }));
  }, [jobPostsList]);

  // Load job posts list — только в режиме создания, в edit job-post управляется через JobApplication
  useEffect(() => {
    if (currentTalentId) return;
    (async () => {
      try {
        const response = await jobs.listPosts();
        const payload = response?.data;
        let items = [];
        if (Array.isArray(payload)) items = payload;
        else if (Array.isArray(payload?.data)) items = payload.data;
        else if (Array.isArray(payload?.rows)) items = payload.rows;
        else if (Array.isArray(payload?.items)) items = payload.items;
        setJobPostsList(items);
      } catch (err) {
        console.error('Error fetching job posts:', err);
      }
    })();
  }, [currentTalentId]);

  // Статусы, допустимые на ВЫБРАННОЙ стадии — не весь список по типу
  // таланта. Зависит от current_stage_id, а не talent_type: стадия меняется
  // (авто при смене типа, вручную позже), и список статусов должен ехать
  // следом за ней. Пока стадия ещё не выбрана (доля секунды на создании, пока
  // не сработал авто-выбор ниже), список пуст — это нормально, эффект,
  // зависящий от current_stage_id, отработает следом в этом же цикле рендеров.
  const fetchStatusesForStage = useCallback(async () => {
    if (isMyProfile) return;
    if (!formData.current_stage_id) return;
    try {
      const statusesResponse = await stages.getStatusesForStage(formData.current_stage_id);
      if (statusesResponse && statusesResponse.data) {
        const list = statusesResponse.data;
        setStatusesList(list);

        if (!initialData || !initialData.id) {
          // Первичный выбор при создании — один раз за жизнь формы
          const firstStatus = list[0];
          if (firstStatus && !statusAutoSelectedRef.current) {
            statusAutoSelectedRef.current = true;
            setFormData(prev => ({ ...prev, current_status_id: firstStatus.id }));
          }
        } else {
          // Стадию сменили вручную на уже существующем таланте: если текущий
          // статус на новой стадии недопустим, подставляем дефолт стадии —
          // то же самое сделал бы серверный хук reconcileStageAndStatus, но
          // тут ещё и не показываем в SingleSelect значение, которого нет
          // среди options.
          setFormData(prev => {
            const stillValid = list.some((s) => s.id === prev.current_status_id);
            if (stillValid || list.length === 0) return prev;
            return { ...prev, current_status_id: list[0].id };
          });
        }
      }
    } catch (err) {
      console.error('Error fetching statuses for stage:', err);
      setError('Error loading statuses list');
    }
  }, [formData.current_stage_id, isMyProfile, initialData?.id]);

  useEffect(() => {
    fetchStatusesForStage();
  }, [fetchStatusesForStage]);

  // Reset auto-select flag when talent_type changes so the new type's first status gets selected
  useEffect(() => {
    statusAutoSelectedRef.current = false;
  }, [formData.talent_type]);

  // Load the stage list once — it's global, not scoped to talent_type
  useEffect(() => {
    if (isMyProfile) return;
    (async () => {
      try {
        const stagesResponse = await stages.getStages();
        if (stagesResponse && stagesResponse.data) {
          setStagesList(stagesResponse.data);
        }
      } catch (err) {
        console.error('Error fetching stages:', err);
      }
    })();
  }, [isMyProfile]);

  // Стадия задаёт тип таланта, поэтому поля держим согласованными: когда
  // выбранная стадия не относится к выбранному типу (новый талант без стадии
  // или в форме сменили тип), ставим первую по порядку стадию этого типа.
  // Смена типа у существующего таланта — это и есть перевод на такую стадию;
  // причину спросит модалка смены стадии при сохранении.
  useEffect(() => {
    if (isMyProfile || stagesList.length === 0 || !formData.talent_type) return;

    const currentStage = stagesList.find((stage) => stage.id === Number(formData.current_stage_id));
    if (currentStage && currentStage.talent_type === formData.talent_type) return;

    // Талант, чей тип исторически разошёлся со стадией: пока ни тип, ни стадию
    // в форме не трогали, оставляем как есть
    if (
      formData.current_stage_id &&
      initialData?.id &&
      formData.talent_type === initialData.talent_type &&
      Number(formData.current_stage_id) === Number(initialData.current_stage_id)
    ) return;

    const defaultStage = [...stagesList]
      .sort((a, b) => (a.stage_order || 0) - (b.stage_order || 0))
      .find((stage) => stage.talent_type === formData.talent_type);
    if (defaultStage && defaultStage.id !== Number(formData.current_stage_id)) {
      stageAutoSelectedRef.current = true;
      setFormData(prev => ({ ...prev, current_stage_id: defaultStage.id }));
    }
  }, [stagesList, isMyProfile, initialData?.id, initialData?.talent_type, initialData?.current_stage_id, formData.talent_type, formData.current_stage_id]);

  // Генерация предпросмотра talent_id при создании нового таланта
  const generateTalentIdPreview = (talentType, departmentCode) => {
    const typeCodes = { employee: 'EMP', presale: 'PRS', candidate: 'CND' };
    const typeCode = typeCodes[talentType] || talentType?.toUpperCase().slice(0, 3) || '???';
    if (departmentCode) {
      return `${typeCode}-${departmentCode.toUpperCase()}-###`;
    }
    return `${typeCode}-###`;
  };

  useEffect(() => {
    if (currentTalentId) return;
    const primaryProfession = professions.find(p => p.is_primary) || professions[0];
    const deptCode = primaryProfession?.department_code
      || departmentsOptions.find(d => d.id === primaryProfession?.department_id)?.code
      || null;
    const preview = generateTalentIdPreview(formData.talent_type, deptCode);
    setFormData(prev => ({ ...prev, talent_id: preview }));
  }, [formData.talent_type, professions, departmentsOptions, currentTalentId]);

  // Загрузка справочных данных — все параллельно, каждый селект активируется как только его данные придут
  useEffect(() => {
    fetchCountries();
    fetchAllCities();
    fetchTools();
    fetchResponsibilities();
    fetchContactTools();
    fetchProfessions();
    fetchSimilarTerms();
    fetchDepartments();
    fetchPositions();
    fetchIndustries();
    fetchSubIndustries();
    fetchLanguages();
    fetchLanguageLevels();
    fetchCurrencies();
    fetchShifts();
    fetchRates();
    fetchUsersNotInTalents();
    fetchManagers();
  }, []);

  // Auto-initialize 3 contact cards (Phone, Email, Telegram) when creating new talent
  useEffect(() => {
    const isCreating = !initialData?.id;
    const hasInitialContacts = initialData?.contacts && initialData.contacts.length > 0;

    if (isCreating && !hasInitialContacts && contactToolsOptions.length > 0 && contacts.length === 0) {
      // Find Phone, Email, and Telegram tools
      const phoneType = contactToolsOptions.find(tool => tool.name.toLowerCase() === 'phone');
      const emailType = contactToolsOptions.find(tool => tool.name.toLowerCase() === 'email');
      const telegramType = contactToolsOptions.find(tool => tool.name.toLowerCase() === 'telegram');

      const initialContacts = [];

      // Add Phone contact (primary by default)
      if (phoneType) {
        initialContacts.push({
          tool_id: phoneType.id,
          value: '',
          is_primary: true,
          is_verified: false
        });
      }

      // Add Email contact
      if (emailType) {
        initialContacts.push({
          tool_id: emailType.id,
          value: '',
          is_primary: false,
          is_verified: false
        });
      }

      // Add Telegram contact
      if (telegramType) {
        initialContacts.push({
          tool_id: telegramType.id,
          value: '',
          is_primary: false,
          is_verified: false
        });
      }

      // Only set contacts if we found at least one tool
      if (initialContacts.length > 0) {
        setContacts(initialContacts);
      }
    }
  }, [contactToolsOptions, initialData?.id, initialData?.contacts]);

  // Load saved form data from localStorage (only when creating, not editing)
  useEffect(() => {
    if (!isFormInitialized.current && contactToolsOptions.length > 0) {
      if (!isEditing) {
        // Try to restore data from localStorage
        const savedData = loadFromStorage();
        if (savedData) {
          // Set flag to prevent auto-fill from overwriting restored data
          setDataRestoredFromStorage(true);

          // Restore formData
          if (savedData.formData) {
            setFormData(prev => ({
              ...prev,
              ...savedData.formData,
              // Keep manager_id from user if it was set
              manager_id: savedData.formData.manager_id || prev.manager_id
            }));
          }

          // Restore contacts
          if (savedData.contacts && savedData.contacts.length > 0) {
            setContacts(savedData.contacts);
          }

          // Restore professions
          if (savedData.professions && savedData.professions.length > 0) {
            setProfessions(savedData.professions);
          }

          // Restore selectedTools
          if (savedData.selectedTools && savedData.selectedTools.length > 0) {
            setSelectedTools(savedData.selectedTools);
          }

          // Restore selectedResponsibilities
          if (savedData.selectedResponsibilities && savedData.selectedResponsibilities.length > 0) {
            setSelectedResponsibilities(savedData.selectedResponsibilities);
          }

          // Restore cv
          if (savedData.cv && savedData.cv.length > 0) {
            setCV(savedData.cv);
          }

          // Restore languages
          if (savedData.languages && savedData.languages.length > 0) {
            setLanguages(savedData.languages);
          }

          // Restore important dates
          if (savedData.importantDates && savedData.importantDates.length > 0) {
            setImportantDates(savedData.importantDates);
          }

          // Restore AI settings
          if (savedData.aiSettings && savedData.aiSettings.length > 0) {
            setAiSettings(savedData.aiSettings);
          }

          // Restore publicSettings
          if (savedData.publicSettings) {
            setPublicSettings(savedData.publicSettings);
          }

          // Restore AI parsed data panel state
          if (savedData.aiRawData) {
            setAiRawData(savedData.aiRawData);
          }
          if (savedData.unmappedData) {
            setUnmappedData(savedData.unmappedData);
          }

          // If country was saved, fetch cities for that country
          if (savedData.formData?.country_id) {
            fetchCities(savedData.formData.country_id);
          }
        }
      }

      // Mark form as initialized (data either restored or no data to restore)
      isFormInitialized.current = true;
    }
  }, [contactToolsOptions.length, isEditing, loadFromStorage]);

  // Auto-save form data to localStorage (with debounce)
  // Only save after form is initialized to avoid overwriting stored data during mount
  useEffect(() => {
    if (!isEditing && isFormInitialized.current) {
      saveToStorage({
        formData,
        contacts,
        professions,
        selectedTools,
        selectedResponsibilities,
        cv,
        languages,
        importantDates,
        aiSettings,
        publicSettings,
        aiRawData,
        unmappedData
      });
    }
  }, [
    formData,
    contacts,
    professions,
    selectedTools,
    selectedResponsibilities,
    cv,
    languages,
    importantDates,
    aiSettings,
    publicSettings,
    aiRawData,
    unmappedData,
    isEditing,
    saveToStorage
  ]);


  const fetchCountries = async () => {
    try {
      const response = await libraries.getCountries();
      setCountryOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  };

  const fetchCities = async (countryId) => {
    if (!countryId) {
      setCityOptions(allCities); // Показываем все города если страна не выбрана
      setShowAllCities(true);
      return;
    }

    try {
      setLoadingCities(true);
      const response = await libraries.getCitiesByCountry(countryId);
      setCityOptions(normalizeResponse(response));
      setShowAllCities(false); // Показываем только города выбранной страны
    } catch (err) {
      console.error('Error fetching cities:', err);
      setCityOptions(allCities); // В случае ошибки показываем все города
      setShowAllCities(true);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchAllCities = async () => {
    try {
      const response = await libraries.getAllCities();
      const cities = normalizeResponse(response);
      setAllCities(cities);
      setCityOptions(cities);
      setShowAllCities(true);
    } catch (err) {
      console.error('Error fetching all cities:', err);
    }
  };

  const fetchTools = async () => {
    try {
      const response = await libraries.getTools();
      setToolsOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching tools:', err);
    }
  };

  const fetchResponsibilities = async () => {
    try {
      const response = await libraries.getResponsibilities();
      setResponsibilitiesOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching responsibilities:', err);
    }
  };

  const fetchContactTools = async () => {
    try {
      const response = await libraries.getContactTools();
      setContactToolsOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching contact tools:', err);
    }
  };

  const fetchProfessions = async () => {
    try {
      const response = await libraries.getProfessions();
      setAllProfessionsOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching professions:', err);
    }
  };

  const fetchSimilarTerms = async () => {
    try {
      const response = await libraries.getAllSimilarTerms();
      setAllSimilarTermsOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching similar terms:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await libraries.getDepartments();
      setDepartmentsOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await libraries.getPositions();
      setPositionsOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching positions:', err);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await libraries.getIndustries();
      setIndustriesOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching industries:', err);
    }
  };

  const fetchSubIndustries = async () => {
    try {
      const response = await libraries.getSubIndustries();
      const subIndustries = normalizeResponse(response);
      setSubIndustriesOptions(subIndustries);
      setAllSubIndustriesOptions(subIndustries);
    } catch (err) {
      console.error('Error fetching sub-industries:', err);
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await libraries.getLanguages();
      setLanguagesOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching languages:', err);
    }
  };

  const fetchLanguageLevels = async () => {
    try {
      const response = await libraries.getLanguageLevels();
      setLanguageLevelsOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching language levels:', err);
    }
  };

  const fetchCurrencies = async () => {
    if (isMyProfile) return;
    try {
      const response = await libraries.getCurrencies();
      setCurrencyOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching currencies:', err);
    }
  };

  const fetchShifts = async () => {
    if (isMyProfile) return;
    try {
      const response = await libraries.getShifts();
      setShiftOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching shifts:', err);
    }
  };

  const fetchRates = async () => {
    if (isMyProfile) return;
    try {
      const response = await libraries.getRates();
      setRateOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching rates:', err);
    }
  };

  // Auto-add English language for new talents after both lists are loaded
  useEffect(() => {
    if (!initialData?.id && languages.length === 0 && languagesOptions.length > 0 && languageLevelsOptions.length > 0) {
      const englishLanguage = languagesOptions.find(
        lang => lang.name?.toLowerCase() === 'english' || lang.code?.toLowerCase() === 'en'
      );

      if (englishLanguage) {
        setLanguages([{
          language_id: englishLanguage.id,
          level_id: null
        }]);
      }
    }
  }, [languagesOptions.length, languageLevelsOptions.length, initialData?.id, languages.length]);

  // Function to filter sub-industries by selected industries for a specific CV entry
  const filterSubIndustriesForCV = (cvIndex, selectedIndustryIds) => {
    if (!selectedIndustryIds || selectedIndustryIds.length === 0) {
      // If no industries selected, show all sub-industries
      return allSubIndustriesOptions;
    }

    // Filter sub-industries by selected industries
    // Assumes sub-industries have industry_id field for linking to industry
    const filteredSubIndustries = allSubIndustriesOptions.filter(subIndustry => {
      return selectedIndustryIds.includes(subIndustry.industry_id);
    });

    return filteredSubIndustries;
  };

  const fetchUsersNotInTalents = async () => {
    if (isMyProfile) return;
    try {
      const params = { limit: 100 };
      if (initialData && initialData.id) {
        params.exclude_talent_id = initialData.id;
      }
      const response = await users.getUsersNotInTalents(params);
      setUsersOptions(normalizeResponse(response));
    } catch (err) {
      console.error('Error fetching users not in talents:', err);
    }
  };

  const fetchManagers = async () => {
    if (isMyProfile) return;
    try {
      const response = await users.getAllUsers();

      let managersList = [];
      if (response && response.success && response.data) {
        managersList = Array.isArray(response.data) ? response.data : response.data.users || [];
      }

      setManagerOptions(managersList);
    } catch (err) {
      console.error('Error fetching managers:', err);
    }
  };

   // Form validation
   const validateForm = () => {
     const errors = {};

     if (!formData.first_name.trim()) {
       errors.first_name = 'First name is required';
     }

     if (!formData.last_name.trim()) {
       errors.last_name = 'Last name is required';
     }

     if (!formData.current_status_id || formData.current_status_id === '' || formData.current_status_id === 0) {
       errors.current_status_id = 'Status is required';
     }

     if (!formData.current_stage_id || formData.current_stage_id === '' || formData.current_stage_id === 0) {
       errors.current_stage_id = 'Stage is required';
     }

     if (!isMyProfile && (!formData.manager_id || formData.manager_id === '')) {
       errors.manager_id = 'Manager is required';
     }

     // Validate talent_id (skip validation if it's a preview with ### placeholder)
     if (formData.talent_id && formData.talent_id.trim() && !formData.talent_id.includes('###')) {
       const talentIdRegex = /^[A-Za-z0-9_-]+$/;
       if (!talentIdRegex.test(formData.talent_id.trim())) {
         errors.talent_id = 'Talent ID can only contain letters, numbers, hyphens and underscores';
       } else if (formData.talent_id.trim().length < 1 || formData.talent_id.trim().length > 50) {
         errors.talent_id = 'Talent ID must be between 1 and 50 characters';
       }
     }

     // CV validation - company_name and cv_type are required
     cv.forEach((cvItem, index) => {
       if (!cvItem.company_name || !cvItem.company_name.trim()) {
         errors[`cv_${index}_company`] = 'Company name is required';
       }
       if (!cvItem.cv_type || !cvItem.cv_type.trim()) {
         errors[`cv_${index}_cv_type`] = 'Record type is required';
       }
     });

     // Contact validation - if tool_id or value is provided, both are required
     contacts.forEach((contact, index) => {
       // Если указан tool_id или value, то оба поля обязательны
       const hasToolId = contact.tool_id && contact.tool_id !== '';
       const hasValue = contact.value && contact.value.trim() !== '';

       if (hasToolId && !hasValue) {
         errors[`contact_${index}_value`] = 'Contact value is required';
       }
       if (hasValue && !hasToolId) {
         errors[`contact_${index}_tool_id`] = 'Contact type is required';
       }
     });

     // Profession validation - all three fields are required for each profession in array
     professions.forEach((profession, index) => {
       // Для каждой профессии в массиве все три поля обязательны
       if (!profession.department_id || profession.department_id === '') {
         errors[`profession_${index}_department_id`] = 'Department is required';
       }
       if (!profession.profession_id || profession.profession_id === '') {
         errors[`profession_${index}_profession_id`] = 'Profession is required';
       }
     });

     // Primary profession: only one can be primary
     const primaryCount = professions.filter(p => p.is_primary).length;
     if (primaryCount > 1) {
       errors.professions_primary = 'Only one profession can be marked as primary';
     }

     // Language validation
     languages.forEach((language, index) => {
       if (!language.language_id) {
         errors[`language_${index}_language_id`] = 'Language is required';
       }
       if (!language.level_id) {
         errors[`language_${index}_level_id`] = 'Level is required';
       }
     });

     setValidationErrors(errors);
     return errors;
   };

  // Обработка изменений в форме
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Очищаем ошибку валидации для этого поля
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Функция для заполнения формы данными (используется после резолюции конфликтов)
  const fillFormWithMappedData = (mappedData, conflictResolutions = {}) => {
    try {
      // Подсчет новых данных для notification
      const counts = {
        contacts: 0,
        professions: 0,
        languages: 0,
        tools: 0,
        cv: 0
      };

      // Заполняем основные поля формы с учетом резолюции конфликтов
      setFormData(prev => {
        const updated = { ...prev };
        
        // Name resolution
        if (conflictResolutions.name === 'resume') {
          updated.first_name = mappedData.formData.first_name || prev.first_name;
          updated.last_name = mappedData.formData.last_name || prev.last_name;
        } else {
          // Keep existing (default)
          updated.first_name = prev.first_name || mappedData.formData.first_name;
          updated.last_name = prev.last_name || mappedData.formData.last_name;
        }
        
        // Location resolution
        if (conflictResolutions.location === 'resume') {
          updated.country_id = mappedData.formData.country_id || prev.country_id;
          updated.city_id = mappedData.formData.city_id || prev.city_id;
        } else {
          // Keep existing (default)
          updated.country_id = prev.country_id || mappedData.formData.country_id;
          updated.city_id = prev.city_id || mappedData.formData.city_id;
        }
        
        return updated;
      });

      // Заполняем контакты (3 фазы: заполнить пустые, добавить новые, удалить пустые)
      if (mappedData.contacts && mappedData.contacts.length > 0) {
        const toolsMap = new Map(contactToolsOptions.map(t => [t.id, t]));

        // Фаза 1: Map парсера по tool_id (для заполнения пустых слотов)
        const parserByToolId = new Map();
        for (const c of mappedData.contacts) {
          if (!c || !c.tool_id || !c.value || !c.value.trim()) continue;
          const key = String(c.tool_id);
          if (!parserByToolId.has(key)) parserByToolId.set(key, []);
          parserByToolId.get(key).push(c);
        }

        // Фаза 1: Заполняем пустые существующие контакты (не перезаписываем заполненные)
        let filledCount = 0;
        const updatedContacts = contacts.map(existing => {
          if (existing.value && existing.value.trim()) return existing;
          const key = String(existing.tool_id);
          const arr = parserByToolId.get(key);
          if (arr && arr.length > 0) {
            const used = arr.shift();
            filledCount++;
            return { ...existing, value: used.value.trim() };
          }
          return existing;
        });

        // Фаза 2: Оставшиеся контакты парсера — добавляем как новые (с дедупликацией)
        const remainingParser = [];
        parserByToolId.forEach(arr => remainingParser.push(...arr));

        const existingValues = new Set(updatedContacts.map(c => {
          const contactTool = toolsMap.get(c.tool_id);
          const contactType = contactTool?.name?.toLowerCase() || '';
          return `${String(c.tool_id)}-${normalizeContactValue(contactType, c.value || '')}`;
        }));
        const newContacts = remainingParser.filter(c => {
          const contactTool = toolsMap.get(c.tool_id);
          const contactType = contactTool?.name?.toLowerCase() || '';
          const normalizedValue = normalizeContactValue(contactType, c.value);
          return !existingValues.has(`${String(c.tool_id)}-${normalizedValue}`);
        });

        // Фаза 3: Удаляем контакты, которые остались пустыми
        const result = [...updatedContacts, ...newContacts].filter(
          c => c.value && c.value.trim()
        );

        counts.contacts = filledCount + newContacts.length;
        setContacts(result);
      }

      // Заполняем профессии
      if (mappedData.professions && mappedData.professions.length > 0) {
        const parserProfessionsMap = new Map(
          mappedData.professions.map(p => [p.profession_id, p])
        );
        const departmentsMap = new Map(departmentsOptions.map(dept => [dept.id, dept]));
        const professionsMap = new Map(allProfessionsOptions.map(prof => [prof.id, prof]));

        const updatedProfessions = professions.map(existingProf => {
          const parserProf = parserProfessionsMap.get(existingProf.profession_id);
          if (parserProf && parserProf.department_id &&
              (!existingProf.department_id || !existingProf.department_name)) {
            const department = departmentsMap.get(parserProf.department_id);
            return {
              ...existingProf,
              department_id: parserProf.department_id,
              department_name: department?.name || existingProf.department_name
            };
          }
          return existingProf;
        });

        const existingIds = new Set(updatedProfessions.map(p => p.profession_id));
        const newProfessions = mappedData.professions
          .filter(p => !existingIds.has(p.profession_id))
          .map(p => {
            const fullProf = professionsMap.get(p.profession_id);
            const department = p.department_id ? departmentsMap.get(p.department_id) : null;

            return {
              profession_id: p.profession_id,
              profession_name: fullProf?.name || '',
              department_id: p.department_id || null,
              department_name: department?.name || '',
              position_id: null,
              position_name: '',
              is_primary: false,
              public_visible: true
            };
          });

        // Drop empty placeholder when parser provides real professions
        const base = newProfessions.length > 0
          ? updatedProfessions.filter(p => p.profession_id !== null)
          : updatedProfessions;

        const merged = [...base, ...newProfessions];

        if (merged.length > 0 && !merged.some(p => p.is_primary)) {
          merged[0] = { ...merged[0], is_primary: true };
        }

        counts.professions = newProfessions.length;
        setProfessions(merged);
      }

      // Заполняем языки
      if (mappedData.languages && mappedData.languages.length > 0) {
        const parserLangsMap = new Map(
          mappedData.languages
            .filter(lang => lang.level_id !== null)
            .map(lang => [lang.language_id, lang.level_id])
        );

        const updatedLanguages = languages.map(existingLang => {
          if (existingLang.level_id === null && parserLangsMap.has(existingLang.language_id)) {
            return { ...existingLang, level_id: parserLangsMap.get(existingLang.language_id) };
          }
          return existingLang;
        });

        const existingLangs = new Set(
          updatedLanguages.map(l => `${l.language_id}-${l.level_id || 'null'}`)
        );
        const newLanguages = mappedData.languages.filter(
          l => !existingLangs.has(`${l.language_id}-${l.level_id || 'null'}`)
        );
        counts.languages = newLanguages.length;
        setLanguages([...updatedLanguages, ...newLanguages]);
      }

      // Заполняем tools
      if (mappedData.tools && mappedData.tools.length > 0) {
        const prevToolsSet = new Set(selectedTools);
        const newTools = mappedData.tools.filter(id => !prevToolsSet.has(id));
        counts.tools = newTools.length;
        if (newTools.length > 0) {
          setSelectedTools([...selectedTools, ...newTools]);
        }
      }

      // Заполняем CV (work experience + education) с дедупликацией
      if (mappedData.cv && mappedData.cv.length > 0) {
        const existingCV = new Set(
          cv.map(item =>
            `${item.company_name || 'null'}-${item.start_date || 'null'}-${item.cv_type || 'null'}-${item.specialisation || 'null'}`
          )
        );

        const newCV = mappedData.cv.filter(item => {
          const key = `${item.company_name || 'null'}-${item.start_date || 'null'}-${item.cv_type || 'null'}-${item.specialisation || 'null'}`;
          return !existingCV.has(key);
        });
        counts.cv = newCV.length;
        if (newCV.length > 0) {
          setCV([...cv, ...newCV]);
        }
      }

      // Заполняем salary expectations из expected_rate ТОЛЬКО если еще нет
      if (mappedData.expected_rate && !salary.expectations_amount) {
        const currencyId = mappedData.expected_rate.currency
          ? currencyOptions.find(c => c.symbol === mappedData.expected_rate.currency)?.id || null
          : null;
        setSalary({
          ...salary,
          expectations_amount: mappedData.expected_rate.amount,
          ...(currencyId && { expectations_currency_id: currencyId })
        });
      }

      // Заполняем seo_description ТОЛЬКО если еще нет
      if (mappedData.summary && !publicSettings.seo_description) {
        setPublicSettings(prev => ({
          ...prev,
          seo_description: mappedData.summary
        }));
      }

      // Если была выбрана страна, загружаем города для этой страны
      if (mappedData.formData.country_id) {
        fetchCities(mappedData.formData.country_id);
      }

      // Warnings for unmapped data (items that failed to map)
      const warnings = [];
      if (mappedData.unmapped.professions?.length > 0) {
        warnings.push(`${mappedData.unmapped.professions.length} profession(s) could not be mapped. Check if they exist in database.`);
      }
      if (mappedData.unmapped.languages?.length > 0) {
        warnings.push(`${mappedData.unmapped.languages.length} language(s) could not be mapped. Check language names.`);
      }
      if (mappedData.unmapped.tools?.length > 0) {
        warnings.push(`${mappedData.unmapped.tools.length} tool(s) could not be mapped. Check tool names.`);
      }
      if (mappedData.unmapped.contacts?.length > 0) {
        warnings.push(`${mappedData.unmapped.contacts.length} contact(s) could not be mapped. Check contact types.`);
      }
      if (mappedData.unmapped.country) {
        warnings.push(`Country "${mappedData.unmapped.country}" could not be mapped.`);
      }
      if (mappedData.unmapped.city) {
        warnings.push(`City "${mappedData.unmapped.city}" could not be mapped.`);
      }

      if (warnings.length > 0) {
        setError(`Data processed with warnings: ${warnings.join('; ')}`);
      }

      // Показываем notification о добавленных/обновленных данных
      const messages = [];

      // Рахуємо не тільки нові, а й оновлені елементи
      const processedLanguages = mappedData.languages?.length > 0 ?
        mappedData.languages.length - (mappedData.unmapped.languages?.length || 0) : 0;
      if (processedLanguages > 0) {
        messages.push(`${processedLanguages} language(s)${counts.languages > 0 ? ` (${counts.languages} new)` : ''}`);
      }

      const processedProfessions = mappedData.professions?.length > 0 ?
        mappedData.professions.length - (mappedData.unmapped.professions?.length || 0) : 0;
      if (processedProfessions > 0) {
        messages.push(`${processedProfessions} profession(s)${counts.professions > 0 ? ` (${counts.professions} new)` : ''}`);
      }

      const processedTools = mappedData.tools?.length > 0 ?
        mappedData.tools.length - (mappedData.unmapped.tools?.length || 0) : 0;
      if (processedTools > 0) {
        messages.push(`${processedTools} tool(s)${counts.tools > 0 ? ` (${counts.tools} new)` : ''}`);
      }

      const processedContacts = mappedData.contacts?.length > 0 ?
        mappedData.contacts.length - (mappedData.unmapped.contacts?.length || 0) : 0;
      if (processedContacts > 0) {
        messages.push(`${processedContacts} contact(s)${counts.contacts > 0 ? ` (${counts.contacts} new)` : ''}`);
      }

      if (counts.cv > 0) messages.push(`${counts.cv} CV entries`);

      if (messages.length > 0 && warnings.length === 0) {
        setResumeFillInfo(`Resume data added: ${messages.join(', ')}`);
      } else if (messages.length > 0 && warnings.length > 0) {
        setResumeFillInfo(`Resume data added: ${messages.join(', ')}`);
        // Warnings will be shown separately via setError
      } else if (messages.length === 0 && warnings.length === 0) {
        setError('No new data found in resume or all data already exists');
      }

      // Закрываем drawer после успешного заполнения
      setIsResumeParserOpen(false);
    } catch (error) {
      console.error('Error filling talent form with resume data:', error);
      setError('Error processing resume data. Please try again.');
    }
  };

  // Обработка данных от Resume Parser с проверкой конфликтов
  const handleResumeDataFill = (aiData) => {
    try {
      const mappedData = mapResumeDataToTalentForm(aiData, {
        countryOptions,
        cityOptions,
        allCities,
        professionsOptions: allProfessionsOptions,
        toolsOptions: toolsOptions,
        contactToolsOptions: contactToolsOptions,
        languagesOptions,
        languageLevelsOptions
      });

      // Сохраняем raw AI data и unmapped items для ParsedDataPanel
      setAiRawData(aiData);
      setUnmappedData(mappedData.unmapped);

      // Проверка конфликтов
      const detectedConflicts = {};

      // Name conflict
      const existingName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim();
      const resumeName = `${mappedData.formData.first_name || ''} ${mappedData.formData.last_name || ''}`.trim();
      if (existingName && resumeName && existingName !== resumeName) {
        detectedConflicts.name = {
          existing: existingName,
          resume: resumeName
        };
      }

      // Location conflict
      if (formData.country_id && mappedData.formData.country_id &&
          formData.country_id !== mappedData.formData.country_id) {
        const existingCountry = countryOptions.find(c => c.id === formData.country_id)?.name || 'Unknown';
        const resumeCountry = countryOptions.find(c => c.id === mappedData.formData.country_id)?.name || 'Unknown';
        const existingCity = cityOptions.find(c => c.id === formData.city_id)?.name || '';
        const resumeCity = allCities.find(c => c.id === mappedData.formData.city_id)?.name || '';
        
        detectedConflicts.location = {
          existing: existingCity ? `${existingCountry}, ${existingCity}` : existingCountry,
          resume: resumeCity ? `${resumeCountry}, ${resumeCity}` : resumeCountry
        };
      }

      // Если есть конфликты - показываем діалог
      if (Object.keys(detectedConflicts).length > 0) {
        setConflicts(detectedConflicts);
        setPendingMappedData(mappedData);
        setShowConflictDialog(true);
        setIsResumeParserOpen(false); // Закрываем drawer
        return;
      }

      // Если нет конфликтов - заполняем форму сразу
      fillFormWithMappedData(mappedData);

      // Auto-open parsed data panel after filling
      setIsParsedDataPanelOpen(true);
    } catch (error) {
      console.error('Error processing resume data:', error);
      setError('Error processing resume data. Please try again.');
    }
  };

  // Обработка резолюции конфликтов
  const handleConflictResolve = (selections) => {
    if (pendingMappedData) {
      fillFormWithMappedData(pendingMappedData, selections);
      setPendingMappedData(null);
      setConflicts(null);
      setShowConflictDialog(false);

      // Auto-open parsed data panel after conflict resolution
      setIsParsedDataPanelOpen(true);
    }
  };

  // Обработка отмены конфликтов
  const handleConflictCancel = () => {
    setPendingMappedData(null);
    setConflicts(null);
    setShowConflictDialog(false);
    setIsResumeParserOpen(true); // Возвращаемся к drawer
  };

  // Обработка создания нового tool из ParsedDataPanel
  const handleToolCreated = (toolId, toolName) => {
    // Добавляем tool в toolsOptions, чтобы он появился в MultiSelect
    setToolsOptions(prev => [...prev, { id: toolId, name: toolName }]);

    // Добавляем tool к выбранным
    setSelectedTools(prev => prev.includes(toolId) ? prev : [...prev, toolId]);

    // Удаляем из unmapped списка
    setUnmappedData(prev => ({
      ...prev,
      tools: prev.tools.filter(t => t !== toolName)
    }));
  };

  // Re-map: повторний маппинг aiRawData з поточними options (після створення нових items в бібліотеках)
  const handleRemap = () => {
    if (!aiRawData) return;

    const mappedData = mapResumeDataToTalentForm(aiRawData, {
      countryOptions,
      cityOptions,
      allCities,
      professionsOptions: allProfessionsOptions,
      toolsOptions: toolsOptions,
      contactToolsOptions: contactToolsOptions,
      languagesOptions,
      languageLevelsOptions
    });

    // fillFormWithMappedData handles dedup — safe to call again
    fillFormWithMappedData(mappedData);
    setUnmappedData(mappedData.unmapped);

    const newUnmappedCount = (mappedData.unmapped.tools?.length || 0) +
      (mappedData.unmapped.professions?.length || 0) +
      (mappedData.unmapped.languages?.length || 0) +
      (mappedData.unmapped.contacts?.length || 0) +
      (mappedData.unmapped.country ? 1 : 0) +
      (mappedData.unmapped.city ? 1 : 0);

    if (newUnmappedCount === 0) {
      toast.success('All items mapped successfully!');
    } else {
      toast.success('Re-mapped! Some items still unmapped.');
    }
  };

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationResult = validateForm();
    if (Object.keys(validationResult).length > 0) {
      scrollToFirstError(validationResult);
      return;
    }

    // Валидация: fired/left требуют start_work в списке important dates
    const validDatesForCheck = importantDates.filter(d => d.date_type && d.date_value);
    const departureDate = validDatesForCheck.find(d => DEPARTURE_DATE_TYPES[d.date_type]);
    if (departureDate && !validDatesForCheck.some(d => d.date_type === 'start_work')) {
      setError(`Cannot save a "${DEPARTURE_DATE_TYPES[departureDate.date_type]}" date without a "Start Work" date for this talent.`);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      setResumeFillInfo(null);

      // Подготавливаем данные для отправки (исключаем publicSettings, importantDates и системные поля из formData)
      const { publicSettings: _, importantDates: __, current_status_change_reason: ___, aiSettings: _aiS, currentStatus: _cs, currentStage: _csg, current_stage_change_reason: _cscr, events: _ev, jobApplication: _ja, ...cleanFormData } = formData;
      // Фильтруем и подготавливаем important dates
      const validImportantDates = importantDates.filter(date => date.date_type && date.date_value);

      // Исключаем read-only поля и связанные сущности, не редактируемые в этой форме
      const { export_note, is_active, communications: _omitCommunications, notes: _omitNotes, ...formDataWithoutReadOnly } = cleanFormData;

      // Prepare CV data
      const preparedCV = cv.length > 0 ? prepareCVForSubmission(cv) : [];

      const talentData = {
        ...formDataWithoutReadOnly,
        auth_user_id: cleanFormData.auth_user_id || null,
        // Якщо options ще не завантажились — не фільтруємо, щоб не затерти дані
        tools: [...new Set(selectedTools)]
          .filter(toolId => toolsOptions.length === 0 || toolsOptions.some(tool => tool.id === toolId))
          .map(toolId => ({ tool_id: toolId })),
        responsibilities: [...new Set(selectedResponsibilities)]
          .filter(rId => responsibilitiesOptions.length === 0 || responsibilitiesOptions.some(r => r.id === rId))
          .map(rId => ({ responsibility_id: rId })),
        cv: preparedCV,
        contacts: contacts.length > 0 ? contacts.filter(contact => contact.tool_id && contact.value && contact.value.trim()) : [],
        media: media.length > 0 ? prepareMediaForSubmission(media) : [],
        languages: languages.length > 0 ? languages.filter(lang => lang.language_id && lang.level_id) : [],
        important_dates: validImportantDates.map(date => ({
          id: date.id && typeof date.id === 'number' && date.id > 1000000000000 ? undefined : date.id, // Исключаем временные ID
            date_type: date.date_type,
            date_value: date.date_value,
            description: date.description || null
        })),
        ai_settings: aiSettings.map(setting => ({
          id: setting.id && typeof setting.id === 'number' && setting.id > 1000000000000 ? undefined : setting.id,
          ai_voice_agent_id: setting.ai_voice_agent_id || null
        })),
      };

      // Добавляем salary только при наличии прав
      const hasSalaryPermission = formData.talent_type && (
        hasPermission('talent-service', `${formData.talent_type}_salary`, 'create') ||
        hasPermission('talent-service', `${formData.talent_type}_salary`, 'update')
      );

      if (hasSalaryPermission) {
        talentData.salary = {
          expectations_amount: salary.expectations_amount || null,
          expectations_currency_id: salary.expectations_currency_id || null,
          proposed_salary_amount: salary.proposed_salary_amount || null,
          proposed_salary_currency_id: salary.proposed_salary_currency_id || null,
          shift_id: salary.shift_id || null,
          rate_id: salary.rate_id || null
        };
      }

      const canUpdateTalent = !isMyProfile || (formData.talent_type && hasPermission('talent-service', formData.talent_type, 'update'));

      // Якщо немає прав на редагування — не надсилаємо поля, яких немає у формі,
      // щоб не затерти дані що не завантажувались
      if (!canUpdateTalent) {
        delete talentData.important_dates;
        delete talentData.ai_settings;
      }

      // Профессії та public_settings — тільки за правами
      if (canUpdateTalent) {
        talentData.professions = professions.length > 0 ? prepareProfessionsForSubmission(professions) : [];
      }

      // public_settings — тільки за правами
      if (canUpdateTalent) {
        talentData.public_settings = (currentTalentId || (formData.public_visibility && publicSettings)) ? {
          slug: publicSettings?.slug || '',
          seo_title: publicSettings?.seo_title || '',
          seo_description: publicSettings?.seo_description || '',
          rate_id: publicSettings?.rate_id || null,
          short_name: publicSettings?.short_name || '',
          is_pinned: !!publicSettings?.is_pinned,
          slogan: publicSettings?.slogan || null
        } : null;
      }

      // Сохраняем временные файлы для последующей загрузки
      const tempMediaFiles = media.filter(item => {
        const isTemp = item.is_temp === true || (typeof item.id === 'string' && item.id.startsWith('temp-'));
        const hasFile = item.file && item.file instanceof File;
        return isTemp && hasFile;
      });
      
      const tempUrlMedia = media.filter(item => {
        const isTemp = item.is_temp === true || (typeof item.id === 'string' && item.id.startsWith('temp-'));
        const hasUrl = !item.file && item.file_path;
        return isTemp && hasUrl;
      });

      let submitResult = true;
      let createdTalentId = null;

      if (onSubmit) {
        submitResult = await onSubmit(talentData);
        
        // Если onSubmit возвращает объект с данными таланта, извлекаем ID
        if (submitResult && typeof submitResult === 'object') {
          // Проверяем различные варианты структуры ответа
          if (submitResult.data?.id) {
            createdTalentId = submitResult.data.id;
          } else if (submitResult.data?.data?.id) {
            createdTalentId = submitResult.data.data.id;
          } else if (submitResult.id) {
            createdTalentId = submitResult.id;
          }
        }
      } else {
        // Если это конвертация из Job Application
        if (initialData.job_application_id) {
          const result = await jobApplications.convertToTalent(
            initialData.job_application_id,
            formData.talent_type,
            talentData
          );
          // Проверяем различные варианты структуры ответа
          if (result?.data?.id) {
            createdTalentId = result.data.id;
          } else if (result?.data?.data?.id) {
            createdTalentId = result.data.data.id;
          } else if (result?.id) {
            createdTalentId = result.id;
          }
        } else {
          // Обычное создание таланта
          const result = await talents.createTalent(talentData);
          // Проверяем различные варианты структуры ответа
          if (result?.data?.id) {
            createdTalentId = result.data.id;
          } else if (result?.data?.data?.id) {
            createdTalentId = result.data.data.id;
          } else if (result?.id) {
            createdTalentId = result.id;
          }
        }
      }

      // Проверяем результат onSubmit - если вернули false, значит операция ещё не завершена
      // (например, показывается модал проверки статуса)
      if (submitResult === false) {
        setSaving(false);
        return;
      }

      // Загружаем временные файлы после успешного создания таланта (СИНХРОННО, дожидаемся завершения)
      if (createdTalentId && (tempMediaFiles.length > 0 || tempUrlMedia.length > 0)) {
        try {
          // Загружаем файлы последовательно
          for (const tempFile of tempMediaFiles) {
            try {
              if (!tempFile.file || !(tempFile.file instanceof File)) {
                continue;
              }
              
              if (!createdTalentId) {
                continue;
              }
              
              const result = await talentMedia.uploadMedia(
                createdTalentId,
                tempFile.file,
                tempFile.media_type || 'audio',
                tempFile.public_visible || false,
                tempFile.language_id || null
              );
              
              if (result.success) {
                // Обновляем состояние media, заменяя временный файл на загруженный
                setMedia(prev => prev.map(item => 
                  item.id === tempFile.id ? result.data : item
                ));
              }
            } catch (error) {
              console.error('Error uploading temporary file:', error);
              // Не прерываем процесс, продолжаем загрузку остальных файлов
            }
          }

          // Создаем URL медиа-записи последовательно
          for (const tempUrl of tempUrlMedia) {
            try {
              if (!createdTalentId) {
                continue;
              }
              
              const urlMediaData = {
                talent_id: createdTalentId,
                media_type: tempUrl.media_type || 'audio',
                file_path: tempUrl.file_path,
                file_name: tempUrl.file_name || tempUrl.file_path.split('/').pop() || 'URL Link',
                file_size: 0,
                mime_type: 'text/url',
                public_visible: tempUrl.public_visible || false,
                language_id: tempUrl.language_id || null
              };

              const result = await talentMedia.createMedia(urlMediaData);
              
              if (result.success) {
                // Обновляем состояние media, заменяя временный URL на созданный
                setMedia(prev => prev.map(item => 
                  item.id === tempUrl.id ? result.data : item
                ));
              }
            } catch (error) {
              console.error('Error creating temporary URL media:', error);
              // Не прерываем процесс, продолжаем создание остальных URL
            }
          }
        } catch (error) {
          console.error('Error uploading temporary media files:', error);
          // Не прерываем основной процесс создания таланта
        }
      }

      // Синхронизируем is_active с Auth User Service (/ban | /unban) для привязанного пользователя (не My Profile).
      if (!isMyProfile && talentData.auth_user_id) {
        users.updateUserActivity(talentData.auth_user_id, formData.is_active).catch((err) => {
          console.error('Failed to sync user activity status:', err);
        });
      }

      setSuccess(true);
      setValidationErrors({});
      if (!isMyProfile) {
        toast.success(isEditing ? 'Talent updated successfully' : `Talent ${formData.first_name} ${formData.last_name} created`);
      }

      // Clear localStorage after successful creation
      if (!isEditing) {
        clearStorage();
      }

      // Если это создание нового таланта и есть ID, передаем ID в callback для редиректа
      if (createdTalentId && !currentTalentId && onSuccess) {
        // Передаем ID созданного таланта в callback для редиректа
        onSuccess(createdTalentId);
      } else if (onSuccess) {
        onSuccess();
      }
      
    } catch (err) {
      console.error('Error creating talent:', err);
      console.error('Error response:', err.response);

      // Handle validation errors from backend
      if (err.response?.data?.details && Array.isArray(err.response.data.details)) {
        const serverErrors = {};
        const errorMessages = [];

        err.response.data.details.forEach(error => {
          errorMessages.push(error);

          // Parse validation errors
          if (error.includes('country_id')) {
            serverErrors.country_id = 'Country is required';
          } else if (error.includes('city_id')) {
            serverErrors.city_id = 'City is required';
          } else if (error.includes('Position ID must be a number')) {
            // Find profession index for position error
            const positionMatch = error.match(/professions\[(\d+)\]/);
            if (positionMatch) {
              const professionIndex = parseInt(positionMatch[1]);
              serverErrors[`profession_${professionIndex}_position_id`] = 'Position must be a number';
            }
          } else if (error.includes('position_name') && error.includes('not allowed to be empty')) {
            // Find profession index for position name error
            const positionMatch = error.match(/professions\[(\d+)\]/);
            if (positionMatch) {
              const professionIndex = parseInt(positionMatch[1]);
              serverErrors[`profession_${professionIndex}_position_name`] = 'Position is required';
            }
          }

          // Parse CV field errors: "cv[0].start_date" must be a valid date
          const cvMatch = error.match(/"?cv\[(\d+)\]\.(\w+)"?/);
          if (cvMatch) {
            const cvIndex = parseInt(cvMatch[1]);
            const fieldName = cvMatch[2];

            if (fieldName === 'start_date') {
              serverErrors[`cv_${cvIndex}_start_date`] = 'Start date is required and must be valid';
            } else if (fieldName === 'end_date') {
              serverErrors[`cv_${cvIndex}_end_date`] = 'End date must be valid';
            } else if (fieldName === 'description') {
              serverErrors[`cv_${cvIndex}_description`] = 'Description cannot be empty';
            } else if (fieldName === 'company_name') {
              serverErrors[`cv_${cvIndex}_company`] = 'Company/Institution name is required';
            } else if (fieldName === 'specialisation') {
              serverErrors[`cv_${cvIndex}_specialisation`] = 'Specialisation/Position is required';
            }
          }
        });

        setValidationErrors(serverErrors);
        showError(`Validation error:\n${errorMessages.join('\n')}`);
      } else if (err.response?.data?.message) {
        // Backend error message
        showError(err.response.data.message);
      } else if (err.response?.data?.error) {
        // Alternative error format
        showError(err.response.data.error);
      } else {
        // Generic error
        showError('Error saving talent: ' + err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  // Обработка отмены
  const handleCancel = () => {
    setValidationErrors({});
    // Show confirmation dialog only when creating (not when editing)
    if (!isEditing) {
      setShowCancelConfirm(true);
    } else {
      if (onCancel) {
        onCancel();
      }
    }
  };

  const handleConfirmCancel = () => {
    clearStorage();
    setShowCancelConfirm(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleKeepEditing = () => {
    setShowCancelConfirm(false);
  };

  // Функции для управления связанными сущностями
  const addProfession = () => {
    setProfessions([...professions, {
      profession_id: null,
      profession_name: '',
      department_id: null,
      department_name: '',
      position_id: null,
      position_name: '',
      similar_term_id: null,
      similar_term_name: '',
      is_similar: false,
      is_primary: false,
      public_visible: true
    }]);
  };

  const removeProfession = (index) => {
    // Don't allow removing the last profession
    if (professions.length <= 1) {
      return;
    }
    const removedIsPrimary = professions[index]?.is_primary;
    const updated = professions.filter((_, i) => i !== index);
    if (removedIsPrimary && updated.length > 0 && !updated.some(p => p.is_primary)) {
      updated[0] = { ...updated[0], is_primary: true };
    }
    setProfessions(updated);
  };

  const updateProfession = (index, field, value) => {
    // Primary can only be moved to another profession, not unchecked
    if (field === 'is_primary' && value === false) {
      return;
    }
    const updated = [...professions];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'is_primary' && value === true) {
      updated.forEach((p, i) => {
        if (i !== index) updated[i] = { ...p, is_primary: false };
      });
    }
    setProfessions(updated);
    if (field === 'is_primary' && validationErrors.professions_primary) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next.professions_primary;
        return next;
      });
    }
  };

  // Функция для получения отфильтрованных профессий по департаменту для конкретной профессии
  const getFilteredProfessionsForDepartment = (departmentId) => {
    if (!departmentId) {
      // Если департамент не выбран, показываем все профессии
      return allProfessionsOptions;
    }

    // Фильтруем профессии по департаменту
    return allProfessionsOptions.filter(profession => {
      return profession.department_id === departmentId;
    });
  };


  // Инструменты контактов используют общий список toolsOptions

  const addCV = () => {
    setCV([...cv, {
      company_name: '',
      country_id: null,
      cv_type: 'experiences',
      specialisation: '',
      start_date: '',
      end_date: '',
      description: '',
      public_visible: true,
      professions: [],
      industries: [],
      subIndustries: []
    }]);
  };

  const removeCV = (index) => {
    setCV(cv.filter((_, i) => i !== index));
  };


  const updateCV = (index, field, value) => {
    const updated = [...cv];
    updated[index] = { ...updated[index], [field]: value };
    setCV(updated);

    // Очищаем ошибку валидации для этого поля
    // Для company_name ключ ошибки - cv_${index}_company
    let errorKey = `cv_${index}_${field}`;
    if (field === 'company_name') {
      errorKey = `cv_${index}_company`;
    }

    if (validationErrors[errorKey]) {
      setValidationErrors(prev => ({
        ...prev,
        [errorKey]: undefined
      }));
    }
  };

  // Функции для управления языками
  const addLanguage = () => {
    setLanguages([...languages, {
      language_id: null,
      level_id: null
    }]);
  };

  const removeLanguage = (index) => {
    setLanguages(languages.filter((_, i) => i !== index));
    
    // Очищаем ошибки валидации для удаляемого языка
    const errorsToRemove = {};
    Object.keys(validationErrors).forEach(key => {
      if (key.startsWith(`language_${index}_`)) {
        errorsToRemove[key] = undefined;
      }
    });
    
    if (Object.keys(errorsToRemove).length > 0) {
      setValidationErrors(prev => ({
        ...prev,
        ...errorsToRemove
      }));
    }
  };

  const updateLanguage = (index, field, value) => {
    const updated = [...languages];
    updated[index] = { ...updated[index], [field]: value };
    setLanguages(updated);
    
    // Очищаем ошибки валидации для этого языка
    const errorKey = `language_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors(prev => ({
        ...prev,
        [errorKey]: undefined
      }));
    }
  };

  // Очистка ошибки
  const clearError = () => {
    setError(null);
    setValidationErrors({});
  };

  const showError = (message) => {
    setError(message);
    toast.error(message.split('\n')[0], { duration: 6000 });
    setTimeout(() => {
      errorAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };


  // Подготовка профессий для отправки на сервер
  const prepareProfessionsForSubmission = (professionsToPrepare) => {
    const valid = professionsToPrepare.filter(profession =>
      profession.department_id && profession.profession_id
    );
    const hasPrimary = valid.some(p => p.is_primary);
    const primaryIndices = valid.map((p, i) => (p.is_primary ? i : -1)).filter(i => i >= 0);
    const firstPrimaryIndex = primaryIndices[0];
    return valid.map((profession, index) => {
      const dept = departmentsOptions.find(d => d.id === profession.department_id);
      const baseData = {
        profession_id: profession.profession_id,
        profession_name: profession.profession_name,
        department_id: profession.department_id,
        department_name: profession.department_name,
        department_code: profession.department_code || dept?.code || null,
        position_id: profession.position_id,
        position_name: profession.position_name,
        similar_term_id: profession.similar_term_id || null,
        similar_term_name: profession.similar_term_name || null,
        is_similar: profession.is_similar || false,
        is_primary: hasPrimary ? index === firstPrimaryIndex : index === 0,
        public_visible: profession.public_visible !== false
      };

      // Если профессия уже существует (есть ID), добавляем его для обновления
      if (profession.id) {
        baseData.id = profession.id;
      }

      return baseData;
    });
  };

  // Подготовка CV данных для отправки на сервер
  const prepareCVForSubmission = (cvData) => {
    return cvData
      .filter(cvItem => {
        // Фильтруем только CV записи с обязательными полями
        return cvItem.company_name && cvItem.company_name.trim() && cvItem.cv_type && cvItem.cv_type.trim();
      })
      .map(cvItem => {
        // Очищаем пустые строки и invalid dates, конвертируем в null
        const cleanedItem = { ...cvItem };

        // Country ID - конвертируем пустую строку в null
        if (cleanedItem.country_id === '' || cleanedItem.country_id === undefined) {
          cleanedItem.country_id = null;
        }

        // Specialisation - конвертируем пустую строку в null
        if (!cleanedItem.specialisation || cleanedItem.specialisation.trim() === '') {
          cleanedItem.specialisation = null;
        }

        // Start date - конвертируем пустую строку и invalid date в null
        if (!cleanedItem.start_date || cleanedItem.start_date === '' || cleanedItem.start_date === 'Invalid date') {
          cleanedItem.start_date = null;
        }

        // End date - конвертируем пустую строку и invalid date в null
        if (!cleanedItem.end_date || cleanedItem.end_date === '' || cleanedItem.end_date === 'Invalid date') {
          cleanedItem.end_date = null;
        }

        // Description - конвертируем пустую строку в null
        if (!cleanedItem.description || cleanedItem.description.trim() === '') {
          cleanedItem.description = null;
        }

        return {
          ...cleanedItem,
          professions: cleanedItem.professions
            ? cleanedItem.professions
                .filter(prof => prof.profession_id && prof.profession_name) // Фильтруем только валидные профессии
                .map(prof => {
                  const baseData = {
                    profession_id: prof.profession_id,
                    profession_name: prof.profession_name,
                    department_id: prof.department_id,
                    department_name: prof.department_name
                  };

                  // Если профессия уже существует (есть ID), добавляем его для обновления
                  if (prof.id) {
                    baseData.id = prof.id;
                  }

                  return baseData;
                })
            : []
        };
      });
  };

  const prepareMediaForSubmission = (mediaItems) => {
    // Фильтруем временные файлы - они будут загружены отдельно после создания таланта
    return mediaItems
      .filter(item => {
        if (item.is_temp) return false;
        if (typeof item.id === 'string' && item.id.startsWith('temp-')) return false;
        return true;
      })
      .map(item => {
        const {
          talent_id,
          formatted_file_size,
          file_url,
          is_image,
          is_document,
          is_video,
          is_audio,
          created_at,
          updated_at,
          is_temp,
          file, // Удаляем сам файл из данных
          ...cleanItem
        } = item;

        if (typeof cleanItem.id === 'string' && cleanItem.id.startsWith('temp-')) {
          delete cleanItem.id;
        }

        if (cleanItem.language_id === '') {
          cleanItem.language_id = null;
        }

        return cleanItem;
      });
  };

  // Очистка успеха
  const clearSuccess = () => {
    setSuccess(false);
    setValidationErrors({});
  };

  return (
  <>
    <div className="space-y-6">
      {/* Page header */}
      {showHeader && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center px-3 py-2 border border-light-border-secondary dark:border-dark-border-secondary text-sm font-medium rounded-lg text-light-text-primary dark:text-dark-text-primary bg-light-bg-primary dark:bg-dark-bg-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to List
            </button>
            <div>
              <h1 className="min-w-0 text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">
                {title}
              </h1>
              <p className="mt-1 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Fill out the form to create a new talent profile
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Job Application Info */}
      {jobApplicationInfo && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start">
            <User className="h-5 w-5 text-blue-400 dark:text-blue-300 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Conversion from Job Application</h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                <p><strong>Name:</strong> {jobApplicationInfo.name}</p>
                <p><strong>Status:</strong> {jobApplicationInfo.status?.name || 'Not specified'}</p>
                <p><strong>Manager:</strong> {jobApplicationInfo.manager?.name || 'Not assigned'}</p>
                <p><strong>Created:</strong> {new Date(jobApplicationInfo.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div ref={errorAlertRef} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-1 text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">
                {error}
              </div>
            </div>
            <button
              type="button"
              onClick={clearError}
              className="ml-3 inline-flex items-center justify-center h-5 w-5 text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Resume Fill Info Alert */}
      {resumeFillInfo && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Resume data filled</h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                {resumeFillInfo}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setResumeFillInfo(null)}
              className="ml-3 inline-flex items-center justify-center h-5 w-5 text-green-400 dark:text-green-300 hover:text-green-600 dark:hover:text-green-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Success!</h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                {isMyProfile
                  ? 'Profile updated successfully'
                  : `New talent ${formData.first_name} ${formData.last_name} created successfully`
                }
              </p>
            </div>
            <button
              type="button"
              onClick={clearSuccess}
              className="ml-3 inline-flex items-center justify-center h-5 w-5 text-green-400 dark:text-green-300 hover:text-green-600 dark:hover:text-green-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile navigation */}
      {!isModal && (
        <MobileFormNav
          sections={FORM_SECTIONS}
          activeSection={activeSection}
          completionStatus={completionStatus}
          visibleSections={visibleSectionIds}
          onSectionClick={scrollToSection}
        />
      )}

      {/* Sidebar + Form Container */}
      <div className="flex min-w-0 gap-6">
        {/* Sidebar */}
        {!isModal && (
          <FormSidebar
            sections={FORM_SECTIONS}
            activeSection={activeSection}
            completionStatus={completionStatus}
            visibleSections={visibleSectionIds}
            onSectionClick={scrollToSection}
          />
        )}

        {/* Form Content */}
        <div className="flex-1 min-w-0">
          <div className="relative bg-light-bg-primary dark:bg-dark-bg-primary shadow rounded-lg border border-light-border-primary dark:border-dark-border-primary">
            <form id="talent-form" onSubmit={handleSubmit} className="space-y-8 p-4 sm:p-6">
              {/* Basic Information */}
              <FormSection
                id="basic-info"
                icon={User}
                title="Basic Information"
                headerRight={
                  !isMyProfile && !initialData.id && (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setIsResumeParserOpen(true)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        AI Resume Parser
                      </button>
                      {aiRawData && (
                        <button
                          type="button"
                          onClick={() => setIsParsedDataPanelOpen(true)}
                          className="inline-flex items-center px-3 py-2 border border-light-border-primary dark:border-dark-border-primary text-sm font-medium rounded-lg text-light-text-primary dark:text-dark-text-primary bg-light-bg-secondary dark:bg-dark-bg-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm"
                        >
                          <Info className="h-4 w-4 mr-2" />
                          AI Data
                          {unmappedData && (unmappedData.tools?.length > 0 || unmappedData.professions?.length > 0 || unmappedData.languages?.length > 0 || unmappedData.contacts?.length > 0 || unmappedData.country || unmappedData.city) && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                              {(unmappedData.tools?.length || 0) + (unmappedData.professions?.length || 0) + (unmappedData.languages?.length || 0) + (unmappedData.contacts?.length || 0) + (unmappedData.country ? 1 : 0) + (unmappedData.city ? 1 : 0)}
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  )
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div>
                 <TextField
                   label="Last Name"
                   required
                   fullWidth
                   type="text"
                   name="last_name"
                   value={formData.last_name}
                   onChange={handleInputChange}
                   placeholder="Enter last name"
                   error={!!validationErrors.last_name}
                   helperText={validationErrors.last_name}
                 />
               </div>

               <div>
                 <TextField
                   label="First Name"
                   required
                   fullWidth
                   type="text"
                   name="first_name"
                   value={formData.first_name}
                   onChange={handleInputChange}
                   placeholder="Enter first name"
                   error={!!validationErrors.first_name}
                   helperText={validationErrors.first_name}
                 />
               </div>

               {!isMyProfile && (
                 <div>
                   <TextField
                     label="Talent ID"
                     fullWidth
                     type="text"
                     name="talent_id"
                     value={formData.talent_id || ''}
                     onChange={handleInputChange}
                     placeholder="e.g. EMP-DEV-###"
                     error={!!validationErrors.talent_id}
                     helperText={validationErrors.talent_id || (!currentTalentId ? 'Preview — ### will be replaced with actual ID on save' : undefined)}
                   />
                 </div>
               )}

               <div>
                 <TextField
                   label="Age / Birth Year"
                   fullWidth
                   type="number"
                   name="birth_year"
                   value={formData.birth_year || ''}
                   onChange={handleInputChange}
                   placeholder="e.g. 25 or 1998"
                   min="1"
                   max="2100"
                   helperText="Enter age (e.g. 25) or birth year (e.g. 1998)"
                 />
               </div>

              <div>
                <Autocomplete
                  clearable
                  fullWidth
                  label="Country"
                  value={formData.country_id ? formData.country_id.toString() : ''}
                  onChange={(value) => {
                    const countryId = value ? parseInt(value) : '';
                    setFormData(prev => ({
                      ...prev,
                      country_id: countryId,
                      city_id: '' // Reset city when country changes
                    }));
                    fetchCities(countryId);
                  }}
                  placeholder="Select country"
                  disabled={countryOptions.length === 0}
                  options={countryOptions.map((country) => ({
                    value: String(country.id),
                    label: country.name
                  }))}
                  error={!!validationErrors.country_id}
                />
                {validationErrors.country_id && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.country_id}</p>
                )}
              </div>

              <div>
                <Autocomplete
                  clearable
                  fullWidth
                  label="City"
                  value={formData.city_id ? formData.city_id.toString() : ''}
                  onChange={(value) => {
                    const cityId = value ? parseInt(value) : '';
                    if (cityId) {
                      const selectedCity = allCities.find(city => city.id === cityId);
                      if (selectedCity && selectedCity.country_id) {
                        setFormData(prev => ({
                          ...prev,
                          city_id: cityId,
                          country_id: selectedCity.country_id
                        }));
                        // Load cities for specific country
                        fetchCities(selectedCity.country_id);
                      } else {
                        // If city is selected but country not found, show all cities
                        setFormData(prev => ({ ...prev, city_id: cityId }));
                        setCityOptions(allCities);
                        setShowAllCities(true);
                      }
                    } else {
                      setFormData(prev => ({ ...prev, city_id: cityId }));
                    }
                  }}
                  placeholder="Select city"
                  options={cityOptions.map((city) => ({
                    value: String(city.id),
                    label: city.name
                  }))}
                  disabled={loadingCities}
                  error={!!validationErrors.city_id}
                />
                {validationErrors.city_id && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.city_id}</p>
                )}
                {loadingCities && (
                  <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary mt-1">Loading cities...</div>
                )}
                {!loadingCities && showAllCities && (
                  <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary mt-1">All cities (select a country to filter)</div>
                )}
                {!loadingCities && !showAllCities && (
                  <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">Cities filtered by the selected country</div>
                )}
              </div>

               {!isMyProfile && (
                 <div>
                   <Autocomplete
                     fullWidth
                     label="Talent Type *"
                     value={formData.talent_type}
                     onChange={(value) => {
                       setFormData(prev => ({
                         ...prev,
                         talent_type: value || ''
                       }));
                     }}
                     placeholder="Select talent type"
                     options={getTalentTypeOptions()}
                   />
                 </div>
               )}

               {!isMyProfile && (
                 <div>
                   <Autocomplete
                     clearable
                     fullWidth
                     label="Status *"
                     value={formData.current_status_id ? formData.current_status_id.toString() : ''}
                    onChange={(value) => {
                      const statusId = value ? parseInt(value) : '';
                      const selectedStatus = statusesList.find(status => status.id === statusId);
                      
                      setFormData(prev => {
                        const updates = {
                          ...prev,
                          current_status_id: statusId
                        };
                        
                        // Автоматически обновляем is_active на основе выбранного статуса
                        if (selectedStatus) {
                          updates.is_active = calculateIsActive(selectedStatus);
                        }
                        
                        return updates;
                      });
                      
                      // Clear validation error when status is selected
                      if (validationErrors.current_status_id) {
                        setValidationErrors(prev => ({
                          ...prev,
                          current_status_id: undefined
                        }));
                      }
                    }}
                     placeholder="Select status"
                     disabled={statusOptions.length === 0}
                     options={statusOptions}
                     error={!!validationErrors.current_status_id}
                   />
                   {validationErrors.current_status_id && (
                     <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.current_status_id}</p>
                   )}
                   <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                     Select the current talent status
                   </p>
                 </div>
               )}

               {!isMyProfile && (
                 <div>
                   <Autocomplete
                     clearable
                     fullWidth
                     label="Stage *"
                     value={formData.current_stage_id ? formData.current_stage_id.toString() : ''}
                     onChange={(value) => {
                       const stageId = value ? parseInt(value) : '';
                       setFormData(prev => ({ ...prev, current_stage_id: stageId }));
                       if (validationErrors.current_stage_id) {
                         setValidationErrors(prev => ({ ...prev, current_stage_id: undefined }));
                       }
                     }}
                     placeholder="Select stage"
                     disabled={stageOptions.length === 0}
                     options={stageOptions}
                     error={!!validationErrors.current_stage_id}
                   />
                   {validationErrors.current_stage_id && (
                     <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.current_stage_id}</p>
                   )}
                   <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                     Where the talent is in the pipeline — independent of Status
                   </p>
                 </div>
               )}

               {!isMyProfile && !currentTalentId && (
                 <div>
                   <Autocomplete
                     clearable
                     fullWidth
                     label="Job Post"
                     value={formData.job_post_id ? formData.job_post_id.toString() : ''}
                     onChange={(value) => setFormData(prev => ({ ...prev, job_post_id: value ? parseInt(value) : '' }))}
                     placeholder="Search and select job post (optional)"
                     options={jobPostOptions}
                   />
                   <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                     Link the new talent to a job post (via the auto-created Job Application)
                   </p>
                 </div>
               )}

               {!isMyProfile && (
                 <div>
                   <Autocomplete
                     clearable
                     fullWidth
                     label="User"
                     value={formData.auth_user_id ? formData.auth_user_id.toString() : ''}
                     onChange={(value) => {
                       setFormData(prev => ({
                         ...prev,
                         auth_user_id: value ? parseInt(value) : ''
                       }));
                     }}
                     placeholder="Select user"
                     disabled={usersOptions.length === 0}
                     options={usersOptions.map((user) => ({
                       value: String(user.id),
                       label: `${user.name}`
                     }))}
                   />
                   <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                     Select a user who is not yet in the Talents table
                   </p>
                 </div>
               )}

               {/* Manager */}
               {!isMyProfile && (
                 <div>
                   <Autocomplete
                     clearable
                     fullWidth
                     label="Manager *"
                     value={formData.manager_id ? formData.manager_id.toString() : ''}
                     onChange={(value) => {
                       setFormData(prev => ({
                         ...prev,
                         manager_id: value ? parseInt(value) : ''
                       }));
                       // Clear validation error when manager is selected
                       if (validationErrors.manager_id) {
                         setValidationErrors(prev => ({
                           ...prev,
                           manager_id: undefined
                         }));
                       }
                     }}
                     placeholder="Select manager"
                     disabled={managerOptions.length === 0}
                     options={managerOptions.map((user) => ({
                       value: String(user.id),
                       label: user.name || user.email
                     }))}
                     error={!!validationErrors.manager_id}
                   />
                   {validationErrors.manager_id && (
                     <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.manager_id}</p>
                   )}
                   <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                     Select the manager responsible for this talent
                   </p>
                 </div>
               )}
            </div>
          </FormSection>

          {/* Contacts */}
          <FormSection
            id="contacts"
            icon={Phone}
            title="Contacts"
            headerRight={
              <button
                type="button"
                onClick={() => {
                  setContacts([...contacts, {
                    tool_id: '',
                    value: ''
                  }]);
                }}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </button>
            }
          >
            <ContactForm
              contacts={contacts}
              toolsOptions={contactToolsOptions}
              onContactsChange={setContacts}
              title="Contacts"
              showTitle={false}
              excludeTalentId={currentTalentId || null}
              entityType="talent"
              validationErrors={validationErrors}
              onClearValidationError={(errorKey) => {
                setValidationErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[errorKey];
                  return newErrors;
                });
              }}
            />
          </FormSection>

          {/* Additional Settings */}
          {!isMyProfile && (
            <FormSection id="settings" icon={Settings} title="Additional Settings">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Checkbox
                    label="Public Profile"
                    color="primary"
                    size="small"
                    checked={!!formData.public_visibility}
                    onChange={(checked) => {
                      setFormData(prev => ({ ...prev, public_visibility: checked }));
                    }}
                  />
                  <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                    Profile will be visible on the public website
                  </p>
                </div>

                <div>
                  <Checkbox
                    label="Active"
                    color="primary"
                    size="small"
                    checked={!!formData.is_active}
                    onChange={(checked) => {
                      setFormData(prev => ({ ...prev, is_active: checked }));
                    }}
                  />
                  <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                    Talent is active (automatically updated when status changes, can be manually overridden)
                  </p>
                </div>

              </div>
            </FormSection>
          )}

          {/* Public Settings */}
          {!isMyProfile && formData.public_visibility && (
            <FormSection id="public-settings" icon={Globe} title="Public Settings">
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
                Configure SEO parameters for the public talent profile
              </p>
              <PublicProfileSection
                formMode
                publicSettings={publicSettings}
                setPublicSettings={setPublicSettings}
                firstName={formData.first_name}
                lastName={formData.last_name}
                currentTalentId={currentTalentId || initialData?.id || null}
              />
            </FormSection>
          )}

          {/* Department, Profession and Position */}
          <FormSection
            id="professions"
            icon={Briefcase}
            title="Professions"
            headerRight={
              !isMyProfile && (
                <button
                  type="button"
                  onClick={addProfession}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Profession
                </button>
              )
            }
          >

            {validationErrors.professions_primary && (
              <p className="mb-3 text-sm text-red-600 dark:text-red-400">{validationErrors.professions_primary}</p>
            )}
            {isMyProfile ? (
              // Read-only mode - only profession names
              professions.length > 0 ? (
                <div className="space-y-2">
                  {professions.map((profession, index) => (
                    <div key={index} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg border border-light-border-primary dark:border-dark-border-primary">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <Briefcase className="w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0" />
                        <span className="text-light-text-primary dark:text-dark-text-primary font-medium truncate">
                          {getProfessionName(profession) || 'Profession not specified'}
                        </span>
                        {getPositionName(profession) && (
                          <>
                            <span className="text-gray-400 dark:text-gray-600">•</span>
                            <span className="text-light-text-secondary dark:text-dark-text-secondary truncate">{getPositionName(profession)}</span>
                          </>
                        )}
                      </div>
                      {profession.is_primary && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 self-start sm:self-center">
                          Primary
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">No professions specified</p>
              )
            ) : (
              // Edit mode
              professions.map((profession, index) => (
                <div key={index} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4 mb-4 border border-light-border-primary dark:border-dark-border-primary">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary">Profession {index + 1}</h4>
                    {professions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProfession(index)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Department *
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={profession.department_id ? profession.department_id.toString() : ''}
                      onChange={(value) => {
                        const selectedDepartment = departmentsOptions.find(dept => dept.id.toString() === value);

                        // Update department fields
                        const updated = [...professions];
                        updated[index] = {
                          ...updated[index],
                          department_id: value ? parseInt(value) : null,
                          department_name: selectedDepartment ? selectedDepartment.name : '',
                          department_code: selectedDepartment ? (selectedDepartment.code || null) : null
                        };
                        setProfessions(updated);

                        // Clear validation error
                        if (validationErrors[`profession_${index}_department_id`]) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`profession_${index}_department_id`];
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="Select department"
                      disabled={departmentsOptions.length === 0}
                      options={departmentsOptions.map((dept) => ({
                        value: dept.id.toString(),
                        label: dept.name
                      }))}
                      error={!!validationErrors[`profession_${index}_department_id`]}
                    />
                    {validationErrors[`profession_${index}_department_id`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors[`profession_${index}_department_id`]}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Profession *
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={profession.profession_id ? profession.profession_id.toString() : ''}
                      onChange={(value) => {
                        const selectedProfession = allProfessionsOptions.find(prof => prof.id.toString() === value);
                        
                        // Update both fields simultaneously
                        const updated = [...professions];
                        updated[index] = { 
                          ...updated[index], 
                          profession_id: value ? parseInt(value) : null,
                          profession_name: selectedProfession ? selectedProfession.name : ''
                        };

                        // If profession is selected, automatically set corresponding department
                        if (selectedProfession) {
                          let departmentId = null;
                          let departmentName = '';

                          // Find department by department_id
                          if (selectedProfession.department_id) {
                            const department = departmentsOptions.find(dept => dept.id === selectedProfession.department_id);
                            if (department) {
                              departmentId = department.id;
                              departmentName = department.name;
                            }
                          }

                          // Update department
                          updated[index] = { 
                            ...updated[index], 
                            department_id: departmentId,
                            department_name: departmentName
                          };
                        }

                        setProfessions(updated);

                        // Clear validation error
                        if (validationErrors[`profession_${index}_profession_id`]) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`profession_${index}_profession_id`];
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="Select profession"
                      disabled={allProfessionsOptions.length === 0}
                      options={getFilteredProfessionsForDepartment(profession.department_id).map((prof) => ({
                        value: prof.id.toString(),
                        label: prof.name
                      }))}
                      error={!!validationErrors[`profession_${index}_profession_id`]}
                    />
                    {validationErrors[`profession_${index}_profession_id`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors[`profession_${index}_profession_id`]}</p>
                    )}
                    {validationErrors[`profession_${index}_name`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors[`profession_${index}_name`]}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Position
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={profession.position_id ? profession.position_id.toString() : ''}
                      onChange={(value) => {
                        const selectedPosition = positionsOptions.find(pos => pos.id.toString() === value);
                        
                        // Update both fields simultaneously
                        const updated = [...professions];
                        updated[index] = {
                          ...updated[index],
                          position_id: value ? parseInt(value) : null,
                          position_name: selectedPosition ? selectedPosition.name : ''
                        };
                        setProfessions(updated);

                        // Clear validation error
                        if (validationErrors[`profession_${index}_position_id`]) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[`profession_${index}_position_id`];
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="Select position"
                      disabled={positionsOptions.length === 0}
                      options={positionsOptions.map((pos) => ({
                        value: pos.id.toString(),
                        label: pos.name
                      }))}
                      error={!!validationErrors[`profession_${index}_position_id`]}
                    />
                    {validationErrors[`profession_${index}_position_id`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors[`profession_${index}_position_id`]}</p>
                    )}
                    {validationErrors[`profession_${index}_position_name`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors[`profession_${index}_position_name`]}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Similar Profession
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={profession.similar_term_id ? profession.similar_term_id.toString() : ''}
                      onChange={(value) => {
                        const selectedTerm = allSimilarTermsOptions.find(t => t.id.toString() === value);
                        const updated = [...professions];
                        updated[index] = {
                          ...updated[index],
                          similar_term_id: value ? parseInt(value) : null,
                          similar_term_name: selectedTerm ? selectedTerm.value : '',
                          is_similar: !!value
                        };
                        // Если профессия ещё не выбрана — автоподставить из similar
                        if (selectedTerm?.profession_id && !updated[index].profession_id) {
                          const parentProfession = allProfessionsOptions.find(p => p.id === selectedTerm.profession_id);
                          if (parentProfession) {
                            updated[index].profession_id = parentProfession.id;
                            updated[index].profession_name = parentProfession.name;
                            if (parentProfession.department_id) {
                              const dept = departmentsOptions.find(d => d.id === parentProfession.department_id);
                              if (dept) {
                                updated[index].department_id = dept.id;
                                updated[index].department_name = dept.name;
                                updated[index].department_code = dept.code || null;
                              }
                            }
                          }
                        }
                        setProfessions(updated);
                      }}
                      placeholder="Select similar profession"
                      disabled={allSimilarTermsOptions.length === 0}
                      options={allSimilarTermsOptions
                        .filter(t => !profession.profession_id || t.profession_id === profession.profession_id)
                        .map((term) => ({
                          value: term.id.toString(),
                          label: term.value
                        }))}
                    />
                  </div>

                  <div className="md:col-span-2 flex flex-wrap gap-6">
                    <Checkbox
                      label="Primary Profession"
                      color="primary"
                      size="small"
                      checked={!!profession.is_primary}
                      onChange={(checked) => updateProfession(index, 'is_primary', checked)}
                    />
                    <Checkbox
                      label="Public Visible"
                      color="success"
                      size="small"
                      checked={profession.public_visible !== false}
                      onChange={(checked) => updateProfession(index, 'public_visible', checked)}
                    />
                  </div>
                </div>
              </div>
              ))
            )}
          </FormSection>

          {/* Tools and Technologies */}
          <FormSection id="tools" icon={Wrench} title="Tools & Technologies">
            <Autocomplete
              multiple
              clearable
              fullWidth
              label="Select Tools"
              value={selectedTools.map(String)}
              onChange={(vals) => setSelectedTools((vals || []).map(Number))}
              placeholder="Start typing tool name..."
              disabled={toolsOptions.length === 0}
              options={toolsOptions.map((tool) => ({
                value: String(tool.id),
                label: tool.name
              }))}
            />
          </FormSection>

          {/* Responsibilities */}
          <FormSection id="responsibilities" icon={ClipboardList} title="Responsibilities">
            <Autocomplete
              multiple
              clearable
              fullWidth
              label="Select Responsibilities"
              value={selectedResponsibilities.map(String)}
              onChange={(vals) => setSelectedResponsibilities((vals || []).map(Number))}
              placeholder="Start typing responsibility name..."
              disabled={responsibilitiesOptions.length === 0}
              options={responsibilitiesOptions.map((r) => ({
                value: String(r.id),
                label: r.name
              }))}
            />
          </FormSection>

          {/* CVs */}
          <FormSection
            id="cvs"
            icon={FileText}
            title="CVs"
            headerRight={
              <button
                type="button"
                onClick={addCV}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add CV
              </button>
            }
          >
            {cv.map((cvItem, index) => (
              <div key={index} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4 mb-4 border border-light-border-primary dark:border-dark-border-primary">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary">CV {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeCV(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <TextField
                      label="Company Name"
                      required
                      fullWidth
                      type="text"
                      value={cvItem.company_name}
                      onChange={(e) => updateCV(index, 'company_name', e.target.value)}
                      placeholder={getCVTypePlaceholders(cvItem.cv_type || 'experiences').company_name}
                      error={!!validationErrors[`cv_${index}_company`]}
                      helperText={validationErrors[`cv_${index}_company`]}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Country
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={cvItem.country_id ? cvItem.country_id.toString() : ''}
                      onChange={(value) => updateCV(index, 'country_id', value ? parseInt(value) : null)}
                      placeholder="Select country"
                      disabled={countryOptions.length === 0}
                      options={countryOptions.map((country) => ({
                        value: country.id.toString(),
                        label: country.name
                      }))}
                    />
                  </div>
                  
                  <div>
                    <Autocomplete
                      fullWidth
                      label="Record Type *"
                      value={cvItem.cv_type || 'experiences'}
                      onChange={(value) => updateCV(index, 'cv_type', value || 'experiences')}
                      placeholder="Select record type"
                      options={getCVTypeOptions()}
                      error={!!validationErrors[`cv_${index}_cv_type`]}
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <TextField
                      label="Specialization"
                      fullWidth
                      type="text"
                      value={cvItem.specialisation || ''}
                      onChange={(e) => updateCV(index, 'specialisation', e.target.value)}
                      placeholder={getCVTypePlaceholders(cvItem.cv_type || 'experiences').specialisation}
                      error={!!validationErrors[`cv_${index}_specialisation`]}
                      helperText={validationErrors[`cv_${index}_specialisation`]}
                    />
                  </div>
                  
                  {/* Professions for CV entry */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Professions
                    </label>
                    <Autocomplete
                      multiple
                      clearable
                      fullWidth
                      value={(cvItem.professions ? cvItem.professions.map(prof => prof.profession_id).filter(Boolean) : []).map(String)}
                      onChange={(vals) => {
                        const selectedProfessionIds = (vals || []).map(Number);
                        const updatedProfessions = selectedProfessionIds.map(profId => {
                          const profession = allProfessionsOptions.find(prof => prof.id === profId);
                          return {
                            profession_id: profId,
                            profession_name: profession ? profession.name : '',
                            department_id: profession ? profession.department_id : null,
                            department_name: profession ? profession.department_name : ''
                          };
                        });
                        
                        const updated = [...cv];
                        updated[index] = { 
                          ...updated[index], 
                          professions: updatedProfessions
                        };
                        setCV(updated);
                      }}
                      placeholder="Select professions"
                      disabled={allProfessionsOptions.length === 0}
                      options={allProfessionsOptions.map((prof) => ({
                        value: String(prof.id),
                        label: prof.name
                      }))}
                    />
                  </div>
                  
                  {/* Industries for CV entry */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Industries
                    </label>
                    <Autocomplete
                      multiple
                      clearable
                      fullWidth
                      value={(cvItem.industries || []).map(String)}
                      onChange={(vals) => {
                        const updated = [...cv];
                        updated[index] = {
                          ...updated[index],
                          industries: (vals || []).map(Number),
                          subIndustries: [] // Reset sub-industries when industries change
                        };
                        setCV(updated);
                      }}
                      placeholder="Select industries"
                      disabled={industriesOptions.length === 0}
                      options={industriesOptions.map((industry) => ({
                        value: String(industry.id),
                        label: industry.name
                      }))}
                    />
                  </div>
                  
                  {/* Sub-industries for CV entry */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Sub-industries
                    </label>
                    <Autocomplete
                      multiple
                      clearable
                      fullWidth
                      value={(cvItem.subIndustries || []).map(String)}
                      onChange={(vals) => {
                        const updated = [...cv];
                        updated[index] = {
                          ...updated[index],
                          subIndustries: (vals || []).map(Number)
                        };
                        setCV(updated);
                      }}
                      placeholder="Select sub-industries"
                      options={filterSubIndustriesForCV(index, cvItem.industries || []).map((subIndustry) => ({
                        value: String(subIndustry.id),
                        label: subIndustry.name
                      }))}
                    />
                  </div>
                  
                  <div>
                    <DatePicker
                      label="Start Date"
                      fullWidth
                      value={cvItem.start_date ? new Date(cvItem.start_date) : null}
                      onChange={(d) => updateCV(index, 'start_date', d ? d.toISOString().slice(0, 10) : '')}
                      error={!!validationErrors[`cv_${index}_start_date`]}
                      helperText={validationErrors[`cv_${index}_start_date`]}
                    />
                  </div>

                  <div>
                    <DatePicker
                      label="End Date"
                      fullWidth
                      value={cvItem.end_date ? new Date(cvItem.end_date) : null}
                      onChange={(d) => updateCV(index, 'end_date', d ? d.toISOString().slice(0, 10) : '')}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <TextField
                      label="Responsibilities Description"
                      multiline
                      rows={3}
                      fullWidth
                      value={cvItem.description}
                      onChange={(e) => updateCV(index, 'description', e.target.value)}
                      placeholder="Describe your responsibilities and achievements"
                      error={!!validationErrors[`cv_${index}_description`]}
                      helperText={validationErrors[`cv_${index}_description`]}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Checkbox
                      label="Public Visible"
                      color="success"
                      size="small"
                      checked={cvItem.public_visible !== false}
                      onChange={(checked) => updateCV(index, 'public_visible', checked)}
                    />
                  </div>

                </div>
              </div>
            ))}
          </FormSection>

          {/* Languages */}
          <FormSection
            id="languages"
            icon={Languages}
            title="Languages"
            headerRight={
              <button
                type="button"
                onClick={addLanguage}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Language
              </button>
            }
          >
            {languages.map((language, index) => (
              <div key={index} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4 mb-4 border border-light-border-primary dark:border-dark-border-primary">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary">Language {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeLanguage(index)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Language *
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={language.language_id ? language.language_id.toString() : ''}
                      onChange={(value) => {
                        updateLanguage(index, 'language_id', value ? parseInt(value) : null);
                      }}
                      placeholder="Select language"
                      disabled={languagesOptions.length === 0}
                      options={languagesOptions.map((lang) => ({
                        value: lang.id.toString(),
                        label: lang.name
                      }))}
                      error={!!validationErrors[`language_${index}_language_id`]}
                    />
                    {validationErrors[`language_${index}_language_id`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors[`language_${index}_language_id`]}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                      Proficiency Level *
                    </label>
                    <Autocomplete
                      clearable
                      fullWidth
                      value={language.level_id ? language.level_id.toString() : ''}
                      onChange={(value) => {
                        updateLanguage(index, 'level_id', value ? parseInt(value) : null);
                      }}
                      placeholder="Select level"
                      disabled={languageLevelsOptions.length === 0}
                      options={languageLevelsOptions.map((level) => ({
                        value: level.id.toString(),
                        label: level.name
                      }))}
                      error={!!validationErrors[`language_${index}_level_id`]}
                    />
                    {validationErrors[`language_${index}_level_id`] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors[`language_${index}_level_id`]}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </FormSection>

          {/* Media Files */}
          {(isMyProfile || (formData.talent_type && hasPermission('talent-service', `${formData.talent_type}_media`, 'read'))) && (
            <FormSection id="media" icon={Image} title="Media Files">
              <FileUpload
                talentId={currentTalentId || null}
                existingFiles={media}
                onFileUploaded={(newFile) => {
                  if (Array.isArray(newFile)) {
                    setMedia(newFile);
                  } else {
                    setMedia(prev => [...prev, newFile]);
                  }
                }}
                onFileDeleted={(fileId) => {
                  setMedia(prev => prev.filter(file => file.id !== fileId));
                  queryClient.removeQueries({ queryKey: ['talentMedia', currentTalentId] });
                }}
                onFileUpdated={(updatedFile) => {
                  setMedia(prev => prev.map(file =>
                    file.id === updatedFile.id ? updatedFile : file
                  ));
                }}
                maxFiles={20}
                allowedTypes={[
                  'image/*',
                  'application/pdf',
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'application/vnd.ms-excel',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  'application/vnd.ms-powerpoint',
                  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                  'video/*',
                  'audio/*',
                  'text/plain'
                ]}
                maxSize={50 * 1024 * 1024} // 50MB
                canCreate={isMyProfile || hasPermission('talent-service', `${formData.talent_type}_media`, 'create')}
                canUpdate={!isMyProfile && hasPermission('talent-service', `${formData.talent_type}_media`, 'update')}
                canDelete={!isMyProfile && hasPermission('talent-service', `${formData.talent_type}_media`, 'delete')}
                allowedMediaTypes={isMyProfile ? ['photo', 'resume', 'portfolio'] : null}
              />
            </FormSection>
          )}

          {/* Important Dates */}
          {!isMyProfile && (
          <FormSection
            id="dates"
            icon={Calendar}
            title="Important Dates"
            headerRight={
              <button
                type="button"
                onClick={() => {
                  const newDate = {
                    id: Date.now(),
                    date_type: '',
                    date_value: '',
                    description: '',
                    is_overdue: false,
                    is_upcoming: false,
                    created_at: new Date().toISOString()
                  };
                  setImportantDates([...importantDates, newDate]);
                }}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Date
              </button>
            }
          >
            <ImportantDatesManager
              entityId={currentTalentId}
              entityType="Talent"
              importantDates={importantDates}
              onImportantDatesChange={setImportantDates}
              showTitle={false}
              title="Important Dates"
              className="mt-6"
            />
          </FormSection>
          )}

          {/* AI Settings */}
          {!isMyProfile && (
          <FormSection
            id="ai-settings"
            icon={Bot}
            title="AI Settings"
            headerRight={
              <button
                type="button"
                onClick={() => {
                  const newSetting = {
                    id: Date.now(),
                    ai_voice_agent_id: '',
                    created_at: new Date().toISOString()
                  };
                  setAiSettings([...aiSettings, newSetting]);
                }}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add AI Setting
              </button>
            }
          >
            <AISettingsManager
              entityId={currentTalentId}
              entityType="Talent"
              aiSettings={aiSettings}
              onAISettingsChange={setAiSettings}
              showTitle={false}
              title="AI Settings"
              className="mt-6"
            />
          </FormSection>
          )}

          {/* Salary - visible only with permission */}
          {!isMyProfile && formData.talent_type && hasPermission('talent-service', `${formData.talent_type}_salary`, 'read') && (
          <FormSection id="salary" icon={DollarSign} title="Salary">
            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
              Salary expectations and proposed salary settings
            </p>
            <div className="grid grid-cols-1 gap-4">
              {/* Expectations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <TextField
                    label="Expectations Amount"
                    fullWidth
                    type="number"
                    step="0.01"
                    min="0"
                    value={salary.expectations_amount || ''}
                    onChange={(e) => setSalary({...salary, expectations_amount: e.target.value ? parseFloat(e.target.value) : null})}
                    placeholder="0.00"
                    helperText="Candidate's salary expectations"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Expectations Currency
                  </label>
                  <Autocomplete
                    clearable
                    fullWidth
                    options={currencyOptions.map(c => ({ value: String(c.id), label: `${c.name} (${c.symbol})` }))}
                    value={salary.expectations_currency_id ? salary.expectations_currency_id.toString() : ''}
                    onChange={(value) => setSalary({...salary, expectations_currency_id: value ? parseInt(value) : null})}
                    placeholder="Select currency..."
                    disabled={currencyOptions.length === 0}
                  />
                </div>
              </div>

              {/* Proposed Salary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <TextField
                    label="Proposed Salary Amount"
                    fullWidth
                    type="number"
                    step="0.01"
                    min="0"
                    value={salary.proposed_salary_amount || ''}
                    onChange={(e) => setSalary({...salary, proposed_salary_amount: e.target.value ? parseFloat(e.target.value) : null})}
                    placeholder="0.00"
                    helperText="Company's proposed salary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Proposed Salary Currency
                  </label>
                  <Autocomplete
                    clearable
                    fullWidth
                    options={currencyOptions.map(c => ({ value: String(c.id), label: `${c.name} (${c.symbol})` }))}
                    value={salary.proposed_salary_currency_id ? salary.proposed_salary_currency_id.toString() : ''}
                    onChange={(value) => setSalary({...salary, proposed_salary_currency_id: value ? parseInt(value) : null})}
                    placeholder="Select currency..."
                    disabled={currencyOptions.length === 0}
                  />
                </div>
              </div>

              {/* Shift and Rate */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Shift
                  </label>
                  <Autocomplete
                    clearable
                    fullWidth
                    options={shiftOptions.map(s => ({ value: String(s.id), label: `${s.name} (${s.start_time?.slice(0, 5)} - ${s.end_time?.slice(0, 5)})` }))}
                    value={salary.shift_id ? salary.shift_id.toString() : ''}
                    onChange={(value) => setSalary({...salary, shift_id: value ? parseInt(value) : null})}
                    placeholder="Select shift..."
                    disabled={shiftOptions.length === 0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    Rate
                  </label>
                  <Autocomplete
                    clearable
                    fullWidth
                    options={rateOptions.map(r => ({ value: String(r.id), label: `${r.name} (${r.hours}h, ${r.value})` }))}
                    value={salary.rate_id ? salary.rate_id.toString() : ''}
                    onChange={(value) => setSalary({...salary, rate_id: value ? parseInt(value) : null})}
                    placeholder="Select rate..."
                    disabled={rateOptions.length === 0}
                  />
                </div>
              </div>
            </div>
          </FormSection>
          )}

        </form>

          {/* Sticky footer with action buttons */}
          <FormActions className="sticky bottom-0 z-10 rounded-b-lg border-t border-light-border-primary bg-light-bg-primary px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-2px_8px_rgba(0,0,0,0.06)] dark:border-dark-border-primary dark:bg-dark-bg-primary sm:px-6">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 border border-light-border-secondary dark:border-dark-border-secondary text-sm font-medium rounded-lg text-light-text-primary dark:text-dark-text-primary bg-light-bg-primary dark:bg-dark-bg-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="talent-form"
              disabled={saving}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {submitText}
                </>
              )}
            </button>
          </FormActions>
      </div>
        </div>
      </div>
    </div>
    {/* Resume Parser Drawer */}
    <ResumeParserDrawer
      isOpen={isResumeParserOpen}
      onClose={() => setIsResumeParserOpen(false)}
      onParsed={handleResumeDataFill}
      formType="talent"
    />

    {/* Parsed Data Panel */}
    <ParsedDataPanel
      isOpen={isParsedDataPanelOpen}
      onClose={() => setIsParsedDataPanelOpen(false)}
      aiRawData={aiRawData}
      unmapped={unmappedData}
      onToolCreated={handleToolCreated}
      allowCreateTool={true}
      onRemap={handleRemap}
    />

    {/* Conflict Resolution Dialog */}
    <ResumeConflictDialog
      isOpen={showConflictDialog}
      onClose={handleConflictCancel}
      conflicts={conflicts}
      onResolve={handleConflictResolve}
    />

    {/* Confirm Dialog for Cancel */}
    <ConfirmDialog
      isOpen={showCancelConfirm}
      onClose={handleKeepEditing}
      onConfirm={handleConfirmCancel}
      title="Leave without saving?"
      message="If you leave, any unsaved data will be lost."
      confirmText="Leave"
      cancelText="Stay"
    />
  </>
  );
};

export default TalentForm;
